import { registerSwitchGuard } from '../utils/registerSession';
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import * as RouterDom from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Paper,
  LinearProgress,
  TextField,
  Autocomplete,
  Chip,
  Divider,
  MenuItem,
  Stack,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import {
  AddCircle as AddCircleIcon,
  Delete as DeleteIcon,
  Visibility,
  History as HistoryIcon,
  QrCode,
  Note as NoteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  RestoreFromTrash as RestoreFromTrashIcon,
} from "@mui/icons-material";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { UserContext } from "../UserContext";
import { useBranding } from "../BrandingContext";
import "./EXregister.css";
import ItemHistoryDialog from "./ItemHistoryDialog";
import { prepareInspectionImage, selectInspectionImage } from "../utils/inspectionMedia";
import { resolveChecklistMaxQuestions } from "../utils/checklistConfiguration";
import { withoutRfidFields } from "../utils/rfid";
import {
  alignInspectionDraft,
  classifyUpdateConflict,
  clearMutationIntentAfterError,
  createMutationId,
  getEquipmentTagConflictDetails,
  getOrCreateMutationIntent,
  getRestoreConvergenceItem,
  isMutationReplay,
  isVersionConflictError,
  isRetryableMutationError,
  mergeEquipmentConflict,
  preserveRowsById,
  rebaseDraftOntoRestoredItem,
  rememberMutationIntent,
  withConcurrencyMetadata,
} from "../utils/equipmentConcurrency";
import {
  DataGridPro,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro";
let resizeTimeout = null;
const EMPTY_CLASSIFICATION_STATE = {
  gas_vapor_iec: false,
  gas_vapor_atex: false,
  gas_vapor_nec500: false,
  dust_iec: false,
  dust_atex: false,
  dust_nec500: false,
};
const EMPTY_CHECKLIST_CONFIG = {
  checklistEnabled: false,
  useDefaultQuestions: true,
  questions: [],
  customQuestions: [],
  defaultQuestions: [],
  maxQuestions: 20,
};
const CHECKLIST_ANSWER_OPTIONS = ["Pass", "Fail", "NA"];
const DEFAULT_INSPECTION_VALIDITY_MONTHS = 60;
const REGISTER_VIEW_ACTIVE = "active";
const REGISTER_VIEW_DELETED = "deleted";

function createInspectionDraftSnapshot(item, answers, imageFile) {
  if (!item) return null;

  const file = imageFile?.file;
  return JSON.stringify({
    inspectionType: item.inspection_type || "",
    inspectionResult: item.inspection_result || "",
    failureNotes: item.failure_notes || "",
    imageUuid: item.image_uuid || null,
    replacementImage: file
      ? {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
        }
      : null,
    checklistAnswers: (Array.isArray(answers) ? answers : []).map((entry) => ({
      question: entry.question,
      answer: entry.answer || "",
    })),
  });
}

const bottomActionButtonToneSx = {
  neutral: {
    borderColor: "rgba(255,255,255,0.22)",
    "& .MuiButton-startIcon": { color: "rgba(255,255,255,0.78)" },
    "&:hover": {
      borderColor: "rgba(255,255,255,0.42)",
      backgroundColor: "rgba(255,255,255,0.075)",
    },
  },
  primary: {
    borderColor: "rgba(115,185,235,0.48)",
    "& .MuiButton-startIcon": { color: "#8fc9ee" },
    "&:hover": {
      borderColor: "rgba(115,185,235,0.74)",
      backgroundColor: "rgba(31,111,178,0.13)",
    },
  },
  danger: {
    borderColor: "rgba(255,130,140,0.48)",
    "& .MuiButton-startIcon": { color: "#ffadb6" },
    "&:hover": {
      borderColor: "rgba(255,150,158,0.74)",
      backgroundColor: "rgba(190,45,45,0.15)",
    },
  },
  success: {
    borderColor: "rgba(95,206,139,0.48)",
    "& .MuiButton-startIcon": { color: "#8fdda9" },
    "&:hover": {
      borderColor: "rgba(110,220,153,0.74)",
      backgroundColor: "rgba(47,125,74,0.15)",
    },
  },
};

const bottomActionButtonSx = {
  minHeight: 40,
  px: 1.25,
  borderRadius: 2,
  color: "rgba(255,255,255,0.86)",
  borderColor: "rgba(255,255,255,0.15)",
  backgroundColor: "rgba(255,255,255,0.035)",
  fontSize: 12.5,
  fontWeight: 700,
  whiteSpace: "nowrap",
  "&:hover": {
    borderColor: "rgba(255,255,255,0.30)",
    backgroundColor: "rgba(255,255,255,0.09)",
  },
  "&.Mui-disabled": {
    color: "rgba(255,255,255,0.42)",
    borderColor: "rgba(255,255,255,0.08)",
    backgroundColor: "rgba(255,255,255,0.02)",
    "& .MuiButton-startIcon": { color: "rgba(255,255,255,0.28)" },
  },
  "&:focus-visible, &.Mui-focusVisible": {
    outline: "2px solid var(--register-action-focus, #f4dc21)",
    outlineOffset: 2,
  },
};

function BottomActionButton({ tooltip, disabled, sx, tone = "neutral", ...buttonProps }) {
  const { isBranded, brand } = useBranding();
  return (
    <Tooltip title={tooltip || ""} placement="top" arrow>
      <span style={{ display: "inline-flex" }}>
        <Button
          {...buttonProps}
          disabled={disabled}
          size="small"
          variant="outlined"
          sx={[
            bottomActionButtonSx,
            bottomActionButtonToneSx[tone],
            isBranded && tone === "primary" && {
              borderColor: "rgba(255,255,255,0.42)",
              "& .MuiButton-startIcon": { color: "#ffffff" },
              "&:hover": {
                borderColor: brand.accent,
                backgroundColor: alpha(brand.primary, 0.18),
              },
            },
            sx,
          ]}
        />
      </span>
    </Tooltip>
  );
}

function getInspectionDueBucket(inspectionDate, validityMonths = DEFAULT_INSPECTION_VALIDITY_MONTHS) {
  if (!inspectionDate) return null;

  const parsedInspectionDate = dayjs(String(inspectionDate).split("T")[0]);
  if (!parsedInspectionDate.isValid()) return null;

  const dueDate = parsedInspectionDate.add(validityMonths, "month").startOf("day");
  const today = dayjs().startOf("day");
  const daysUntilDue = dueDate.diff(today, "day");

  if (daysUntilDue < 0) return "overdue";
  if (daysUntilDue <= 30) return "due30";
  if (daysUntilDue <= 60) return "due60";
  return null;
}

function getSelectedStandard(state, group) {
  if (group === "gas") {
    return state.gas_vapor_iec
      ? "iec"
      : state.gas_vapor_atex
      ? "atex"
      : state.gas_vapor_nec500
      ? "nec500"
      : null;
  }
  return state.dust_iec
    ? "iec"
    : state.dust_atex
    ? "atex"
    : state.dust_nec500
    ? "nec500"
    : null;
}

function normalizeClassificationState(state) {
  const gasStandard = getSelectedStandard(state, "gas");
  const dustStandard = getSelectedStandard(state, "dust");
  const normalizedDustStandard =
    gasStandard && dustStandard && gasStandard !== dustStandard
      ? null
      : dustStandard;
  return {
    gas_vapor_iec: gasStandard === "iec",
    gas_vapor_atex: gasStandard === "atex",
    gas_vapor_nec500: gasStandard === "nec500",
    dust_iec: normalizedDustStandard === "iec",
    dust_atex: normalizedDustStandard === "atex",
    dust_nec500: normalizedDustStandard === "nec500",
  };
}
const NavigationContext =
  RouterDom.UNSAFE_NavigationContext || React.createContext(null);
const useLegacyRouteBlocker = (blocker, when = true) => {
  const navigationContext = useContext(NavigationContext);

  useEffect(() => {
    if (!when) {
      return undefined;
    }

    const navigator = navigationContext?.navigator;
    if (!navigator || typeof navigator.block !== "function") {
      return undefined;
    }

    const unblock = navigator.block((tx) => {
      const autoUnblockingTx = {
        ...tx,
        retry() {
          unblock();
          tx.retry();
        },
      };
      blocker(autoUnblockingTx);
    });

    return unblock;
  }, [navigationContext, blocker, when]);
};
function RegisterWorkspace() {
  const { user } = useContext(UserContext);
  const sessionToken = useRef(localStorage.getItem("token")).current;
  const { isBranded, brand } = useBranding();
  const location = useLocation();
  const [loading, setLoading] = React.useState(false);
  const [density, setDensity] = useState(() => {
    return localStorage.getItem("density") || "compact";
  });
  useEffect(() => {
    localStorage.setItem("density", density);
  }, [density]);
  const [equipmentData, setEquipmentData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [registerView, setRegisterView] = useState(REGISTER_VIEW_ACTIVE);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const isEditingAnyRow = useMemo(
    () =>
      Object.values(rowModesModel).some(
        (modeState) => modeState?.mode === "edit"
      ),
    [rowModesModel]
  );
  const [editedRows, setEditedRows] = useState({});
  const editedRowCount = Object.keys(editedRows).length;
  const hasUnsavedChanges = editedRowCount > 0;
  const hasPendingGridChanges = hasUnsavedChanges || isEditingAnyRow;
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const addMutationIntentsRef = useRef(new Map());
  const updateMutationIntentsRef = useRef(new Map());
  const deleteMutationIntentsRef = useRef(new Map());
  const restoreMutationIntentsRef = useRef(new Map());
  const inspectionMutationIntentsRef = useRef(new Map());
  const deletedDraftRecoveriesRef = useRef(new Map());
  const [newItemValues, setNewItemValues] = useState({});
  const [copySearchTag, setCopySearchTag] = useState("");
  const [copySearchModel, setCopySearchModel] = useState("");
  const [selectedCopyItem, setSelectedCopyItem] = useState(null);
  const [openInspectDialog, setOpenInspectDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isBatchSaving, setIsBatchSaving] = useState(false);
  const [isDiscardingChanges, setIsDiscardingChanges] = useState(false);
  const [discardChangesDialogOpen, setDiscardChangesDialogOpen] = useState(false);
  const batchActionInProgress = isBatchSaving || isDiscardingChanges;
  const batchActionInFlightRef = useRef(false);
  const registerStatusRef = useRef(null);
  const [lastInspectionDialogOpen, setLastInspectionDialogOpen] = useState(false);
  const [lastInspectionLoading, setLastInspectionLoading] = useState(false);
  const [lastInspectionData, setLastInspectionData] = useState(null);
  const [lastInspectionImageBlobUrl, setLastInspectionImageBlobUrl] = useState(null);
  const [lastInspectionPhotoState, setLastInspectionPhotoState] = useState("idle");
  const [openQrDialog, setOpenQrDialog] = useState(false);
  const [qrCodeUrls, setQrCodeUrls] = useState([]);
  const [zoomDialogOpen, setZoomDialogOpen] = useState(false);
  const [zoomedImageUrl, setZoomedImageUrl] = useState(null);

  // History dialog state (WEB only)
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyItem, setHistoryItem] = useState(null);
  const [historyError, setHistoryError] = useState("");
  const historyRequestRef = useRef(0);
  useEffect(() => () => { historyRequestRef.current += 1; }, []);
  const [checklistConfig, setChecklistConfig] = useState(EMPTY_CHECKLIST_CONFIG);
  const [checklistConfigLoading, setChecklistConfigLoading] = useState(false);
  const [checklistConfigError, setChecklistConfigError] = useState("");
  const checklistRequestRef = useRef(0);
  const checklistAbortRef = useRef(null);
  const inspectionSourceItemRef = useRef(null);
  const [inspectionChecklistAnswers, setInspectionChecklistAnswers] = useState([]);
  const [passAllChecklistDialogOpen, setPassAllChecklistDialogOpen] = useState(false);
  const [discardInspectionDialogOpen, setDiscardInspectionDialogOpen] = useState(false);
  const inspectionDraftBaselineRef = useRef(null);

  // State for inspect image upload
  const [inspectImageFile, setInspectImageFile] = useState(null);
  const [inspectImagePreviewUrl, setInspectImagePreviewUrl] = useState(null);
  const [inspectExistingImageBlobUrl, setInspectExistingImageBlobUrl] = useState(null);
  const [inspectPhotoState, setInspectPhotoState] = useState("idle");
  const [inspectPhotoError, setInspectPhotoError] = useState("");
  const [inspectUploadProgress, setInspectUploadProgress] = useState(null);
  const inspectionOpenRequestRef = useRef(0);
  const photoRequestRef = useRef(0);
  const photoLoadAbortRef = useRef(null);
  const photoPreparationAbortRef = useRef(null);
  const lastInspectionRequestRef = useRef(0);
  const fileInputRef = useRef(null);
  useEffect(() => () => {
    inspectionOpenRequestRef.current += 1;
    photoRequestRef.current += 1;
    lastInspectionRequestRef.current += 1;
    checklistRequestRef.current += 1;
    checklistAbortRef.current?.abort();
    photoLoadAbortRef.current?.abort();
    photoPreparationAbortRef.current?.abort();
  }, [user?.id, user?.database_name, sessionToken]);
  useEffect(() => {
    setOpenInspectDialog(false);
    setLastInspectionDialogOpen(false);
    setCurrentItem(null);
    setInspectImageFile(null);
    setInspectImagePreviewUrl(null);
    setInspectExistingImageBlobUrl(null);
    setLastInspectionImageBlobUrl(null);
    setInspectPhotoState("idle");
    setInspectPhotoError("");
    inspectionDraftBaselineRef.current = null;
    inspectionSourceItemRef.current = null;
  }, [user?.id, user?.database_name, sessionToken]);
  useEffect(() => () => { if (inspectImagePreviewUrl) URL.revokeObjectURL(inspectImagePreviewUrl); }, [inspectImagePreviewUrl]);
  useEffect(() => () => { if (inspectExistingImageBlobUrl) URL.revokeObjectURL(inspectExistingImageBlobUrl); }, [inspectExistingImageBlobUrl]);
  useEffect(() => () => { if (lastInspectionImageBlobUrl) URL.revokeObjectURL(lastInspectionImageBlobUrl); }, [lastInspectionImageBlobUrl]);
  const inspectionDraftSnapshot = useMemo(
    () =>
      createInspectionDraftSnapshot(
        currentItem,
        inspectionChecklistAnswers,
        inspectImageFile
      ),
    [currentItem, inspectionChecklistAnswers, inspectImageFile]
  );
  const hasInspectionDraftChanges =
    openInspectDialog &&
    inspectionDraftBaselineRef.current !== null &&
    inspectionDraftSnapshot !== inspectionDraftBaselineRef.current;
  const hasPendingPageChanges =
    hasPendingGridChanges || hasInspectionDraftChanges || (openAddDialog && Object.values(newItemValues).some(value => value !== null && value !== undefined && String(value).trim() !== ""));
  useEffect(() => registerSwitchGuard(() => !hasPendingPageChanges || window.confirm("You have unsaved work in this register. Leave it and discard the unsaved changes?")), [hasPendingPageChanges]);
  const confirmRouteLeave = useCallback(
    (tx) => {
      const shouldLeave = window.confirm(
        hasInspectionDraftChanges
          ? "You have an unsaved inspection draft. Leave this page and discard it?"
          : isEditingAnyRow
          ? "Finish or cancel the current row edit before leaving this page. Leave anyway?"
          : "You have unsaved changes. Leave this page without saving?"
      );
      if (shouldLeave) {
        tx.retry();
      }
    },
    [hasInspectionDraftChanges, isEditingAnyRow]
  );
  useLegacyRouteBlocker(confirmRouteLeave, hasPendingPageChanges);

  const [currentPage] = useState(1);
  const [columnWidths, setColumnWidths] = useState(() => {
    return JSON.parse(localStorage.getItem("columnWidths")) || {};
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [classificationState, setClassificationState] = useState(EMPTY_CLASSIFICATION_STATE);
  useEffect(() => {
    async function fetchClassificationSettings() {
      try {
        const token = sessionToken;
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/settings/classification`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data && res.data.length > 0) {
          const row = res.data[0] || EMPTY_CLASSIFICATION_STATE;
          setClassificationState(normalizeClassificationState(row));
        }
      } catch (err) {
        console.error("Error fetching classification:", err);
        toast.error("Failed to fetch classification settings.");
      }
    }
    fetchClassificationSettings();
  }, [sessionToken]);
  const fetchChecklistConfig = useCallback(async () => {
    const request = ++checklistRequestRef.current;
    checklistAbortRef.current?.abort();
    const controller = new AbortController();
    checklistAbortRef.current = controller;
    const token = sessionToken;
    const isCurrent = () => request === checklistRequestRef.current &&
      !controller.signal.aborted && token === localStorage.getItem("token");
    try {
      setChecklistConfigLoading(true);
      setChecklistConfigError("");
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/settings/checklist`,
        { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
      );
      if (!isCurrent()) return null;
      if (typeof res?.data?.checklistEnabled === "boolean") {
        const parsedConfig = {
          checklistEnabled: !!res.data.checklistEnabled,
          useDefaultQuestions: res.data.useDefaultQuestions !== false,
          questions: Array.isArray(res.data.questions) ? res.data.questions : [],
          customQuestions: Array.isArray(res.data.customQuestions)
            ? res.data.customQuestions
            : [],
          defaultQuestions: Array.isArray(res.data.defaultQuestions)
            ? res.data.defaultQuestions
            : [],
          maxQuestions: resolveChecklistMaxQuestions(res.data.maxQuestions),
        };
        setChecklistConfig(parsedConfig);
        return parsedConfig;
      }
      throw new Error("Checklist settings were not returned.");
    } catch (err) {
      if (!isCurrent()) return null;
      setChecklistConfigError("The inspection checklist could not be loaded. Retry before submitting.");
      console.warn("Checklist settings unavailable:", err?.response?.data || err?.message);
      return null;
    } finally {
      if (isCurrent()) setChecklistConfigLoading(false);
    }
  }, [sessionToken]);
  useEffect(() => {
    setChecklistConfig(EMPTY_CHECKLIST_CONFIG);
    fetchChecklistConfig();
  }, [fetchChecklistConfig, user?.id, user?.database_name, sessionToken]);
  const zoneValueOptions = useMemo(() => {
    const options = [];
    if (classificationState.gas_vapor_iec) {
      options.push("Zone 0", "Zone 1", "Zone 2");
    }
    if (classificationState.gas_vapor_atex) {
      options.push("Category 1G", "Category 2G", "Category 3G");
    }
    if (classificationState.gas_vapor_nec500) {
      options.push("Class I Division 1", "Class I Division 2");
    }
    if (classificationState.dust_iec) {
      options.push("Zone 20", "Zone 21", "Zone 22");
    }
    if (classificationState.dust_atex) {
      options.push("Category 1D", "Category 2D", "Category 3D");
    }
    if (classificationState.dust_nec500) {
      options.push("Class II Division 1", "Class II Division 2");
    }
    if (options.length > 0) {
      options.push("Non-Hazardous");
    }
    return options;
  }, [classificationState]);
  const normalizeEditableValue = useCallback((value) => {
    if (value === null || value === undefined || value === "") {
      return "";
    }
    return value;
  }, []);
  const originalRowsById = useMemo(
    () => new Map(originalData.map((row) => [String(row.id), row])),
    [originalData]
  );
  const getPendingCellClassName = useCallback(
    (params) => {
      const originalRow = originalRowsById.get(String(params.id));
      if (!originalRow) {
        return "";
      }
      const currentValue = normalizeEditableValue(params.row?.[params.field]);
      const baselineValue = normalizeEditableValue(originalRow?.[params.field]);
      return currentValue !== baselineValue ? "pending-edit-cell" : "";
    },
    [originalRowsById, normalizeEditableValue]
  );
  const allColumns = useMemo(() => {
    const baseColumns = [
      {
        field: "id",
        headerName: "ID",
        width: 70,
        hide: true,
      },
      {
        field: "old_rfid",
        headerName: "Existing RFID tag ID",
        width: columnWidths["old_rfid"] || 200,
        editable: false,
      },
      {
        field: "rfid_confirmed",
        headerName: "RFID confirmed",
        width: columnWidths["rfid_confirmed"] || 150,
        type: "boolean",
        editable: false,
        valueGetter: (value) => value === true,
        valueFormatter: (value) => value === true ? "true" : "false",
        renderCell: ({ value }) => value === true
          ? <CheckIcon color="success" titleAccess="Confirmed" />
          : <CloseIcon color="disabled" titleAccess="Not yet confirmed" />,
      },
      {
        field: "area",
        headerName: "Area",
        width: columnWidths["area"] || 220,
        editable: true,
      },
      {
        field: "equipment_tag",
        headerName: "Tag",
        width: columnWidths["equipment_tag"] || 170,
        editable: true,
      },
      {
        field: "description",
        headerName: "Description",
        width: columnWidths["description"] || 280,
        editable: true,
      },
      {
        field: "system_component",
        headerName: "System",
        width: columnWidths["system_component"] || 280,
        editable: true,
      },
      {
        field: "manufacturer",
        headerName: "Manufacturer",
        width: columnWidths["manufacturer"] || 220,
        editable: true,
      },
      {
        field: "model_no",
        headerName: "Model no.",
        width: columnWidths["model_no"] || 180,
        editable: true,
      },
      {
        field: "serial_no",
        headerName: "Serial no.",
        width: columnWidths["serial_no"] || 170,
        hide: true,
        editable: true,
      },
      {
        field: "zone",
        headerName: "Zone",
        width: columnWidths["zone"] || 75,
        type: "singleSelect",
        editable: true,
        valueOptions: zoneValueOptions,
      },
      {
        field: "category_epl",
        headerName: "Categ./ EPL",
        width: columnWidths["category_epl"] || 120,
        hide: true,
        editable: true,
      },
      {
        field: "gas_group",
        headerName: "Gas group",
        width: columnWidths["gas_group"] || 80,
        editable: true,
      },
      {
        field: "protection_class",
        headerName: "Protect. class",
        width: columnWidths["protection_class"] || 120,
        editable: true,
      },
      {
        field: "temperature_class",
        headerName: "Temp. class",
        width: columnWidths["temperature_class"] || 100,
        editable: true,
      },
      {
        field: "ip_rating",
        headerName: "IP class",
        width: columnWidths["ip_rating"] || 120,
        hide: true,
        editable: true,
      },
      {
        field: "certifying_authority",
        headerName: "Cert. Auth.",
        width: 150,
        hide: true,
        editable: true,
      },
      {
        field: "certificate_number",
        headerName: "Cert. no",
        width: columnWidths["certificate_number"] || 150,
        hide: true,
        editable: true,
      },
      {
        field: "work_order",
        headerName: "Work order",
        width: columnWidths["work_order"] || 120,
        editable: true,
      },
      {
        field: "edit_date",
        headerName: "Edit Date",
        width: columnWidths["edit_date"] || 120,
        editable: false,
        hide: true,
        renderCell: (params) => {
          const value = params.row.edit_date;
          if (!value) {
            return <span style={{ color: "gray" }}>No Date</span>;
          }
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            return <span style={{ color: "red" }}>Invalid Date</span>;
          }
          return <span>{date.toISOString().split("T")[0]}</span>;
        },
      },
      {
        field: "last_useredit",
        headerName: "Last Edited By",
        width: 150,
        editable: false,
        hide: true,
      },
      {
        field: "inspection_type",
        headerName: "Type",
        width: columnWidths["inspection_type"] || 80,
        editable: false,
      },
      {
        field: "inspection_result",
        headerName: "Result",
        width: columnWidths["inspection_result"] || 110,
        editable: false,
        renderCell: (params) => {
          const value = params.value;
          const style = {
            color: value === "Fail" ? "red" : "inherit",
            fontWeight: value === "Fail" ? "bold" : "normal",
            textAlign: "center",
            width: "100%",
          };
          return <div style={style}>{value}</div>;
        },
      },
      {
        field: "inspection_date",
        headerName: "Insp.Date",
        width: columnWidths["inspection_date"] || 120,
        editable: false,
        renderCell: (params) => {
          const value = params.row.inspection_date;
          if (!value) return <span style={{ color: "gray" }}>No Date</span>;
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            return <span style={{ color: "red" }}>Invalid Date</span>;
          }
          return <span>{date.toISOString().split("T")[0]}</span>;
        },
      },
      {
        field: "inspector",
        headerName: "Inspector",
        width: columnWidths["inspector"] || 200,
        editable: false,
        cellClassName: "inspector-column",
      },
    ];

    if (registerView === REGISTER_VIEW_DELETED) {
      baseColumns.push(
        {
          field: "deleted_by",
          headerName: "Deleted By",
          width: columnWidths["deleted_by"] || 190,
          editable: false,
        },
        {
          field: "deleted_at",
          headerName: "Deleted At",
          width: columnWidths["deleted_at"] || 195,
          editable: false,
          renderCell: (params) => {
            if (!params.value) {
              return <span style={{ color: "gray" }}>No Date</span>;
            }
            const parsed = dayjs(params.value);
            if (!parsed.isValid()) {
              return <span style={{ color: "red" }}>Invalid Date</span>;
            }
            return <span>{parsed.format("YYYY-MM-DD HH:mm")}</span>;
          },
        }
      );
    }

    return baseColumns.map((column) => {
      if (!column.editable) {
        return column;
      }
      const baseCellClassName = column.cellClassName;
      return {
        ...column,
        cellClassName: (params) => {
          const pendingClass = getPendingCellClassName(params);
          if (typeof baseCellClassName === "function") {
            const baseClass = baseCellClassName(params);
            return [baseClass, pendingClass].filter(Boolean).join(" ");
          }
          return [baseCellClassName, pendingClass].filter(Boolean).join(" ");
        },
      };
    });
  }, [columnWidths, zoneValueOptions, getPendingCellClassName, registerView]);
  const editableFieldNames = useMemo(
    () =>
      allColumns.filter((col) => col.editable).map((col) => col.field),
    [allColumns]
  );
  const submitDeletedDraftRestore = useCallback(
    async ({ deletedItem, token, actor }) => {
      const equipmentId = String(deletedItem.id);
      const intentKey = `draft-restore:${equipmentId}`;
      const intent = getOrCreateMutationIntent({
        registry: restoreMutationIntentsRef.current,
        key: intentKey,
        input: {
          id: equipmentId,
          expected_version: deletedItem.version,
          last_useredit: actor,
        },
        buildPayload: (mutationId) => ({
          last_useredit: actor,
          edit_date: new Date().toISOString(),
          expected_version: deletedItem.version,
          mutation_id: mutationId,
        }),
      });

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/equipment/${equipmentId}/restore`,
          intent.payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Client": "web",
            },
          }
        );
        const restoredItem = response.data?.item;
        if (
          !restoredItem ||
          restoredItem.is_deleted ||
          String(restoredItem.id) !== equipmentId
        ) {
          const malformedResponseError = new Error(
            "The server did not return the restored equipment row."
          );
          malformedResponseError.response = response;
          throw malformedResponseError;
        }
        restoreMutationIntentsRef.current.delete(intentKey);
        return {
          restored: true,
          item: restoredItem,
          replayed: isMutationReplay(response.data),
        };
      } catch (error) {
        const convergedItem = getRestoreConvergenceItem(error, equipmentId);
        if (convergedItem) {
          restoreMutationIntentsRef.current.delete(intentKey);
          return { restored: true, item: convergedItem, converged: true };
        }
        clearMutationIntentAfterError(
          restoreMutationIntentsRef.current,
          intentKey,
          error
        );
        return {
          restored: false,
          current: error.response?.data?.current || null,
          error,
          retryable: isRetryableMutationError(error),
          tagConflict: getEquipmentTagConflictDetails(error),
        };
      }
    },
    []
  );
  const submitEquipmentUpdate = useCallback(
    async ({ row, baseRow, fields, token, mutationId, actor, draftLabel = "changes" }) => {
      let candidate = { ...row };
      let comparisonBase = baseRow || row;
      let currentMutationId = mutationId || createMutationId();
      let restoredBeforeSave = false;
      const recoveryKey = String(candidate.id);

      const withCandidateAudit = (merged) => ({
        ...merged,
        ...(candidate.last_useredit !== undefined
          ? { last_useredit: candidate.last_useredit }
          : {}),
        ...(candidate.edit_date !== undefined
          ? { edit_date: candidate.edit_date }
          : {}),
      });

      const recoverDeletedDraft = async (recovery) => {
        for (let restoreAttempt = 0; restoreAttempt < 3; restoreAttempt += 1) {
          const preview = mergeEquipmentConflict({
            base: recovery.base,
            local: candidate,
            server: recovery.deleted,
            fields,
          });

          if (!recovery.approved) {
            const overlapNotice = preview.overlappingFields.length
              ? ` The same fields were also changed elsewhere: ${preview.overlappingFields.join(", ")}.`
              : "";
            const shouldRestore = window.confirm(
              `This item was deleted elsewhere.${overlapNotice}\n\n` +
                `Select OK to restore it now and retry your unsaved ${draftLabel}. ` +
                "Other users' unrelated changes will be preserved. Select Cancel to keep your draft unsaved."
            );
            if (!shouldRestore) {
              recovery.approved = false;
              deletedDraftRecoveriesRef.current.set(recoveryKey, recovery);
              return {
                saved: false,
                item: recovery.deleted,
                pendingItem: candidate,
                authoritativeBase: recovery.deleted,
                mutationId: recovery.updateMutationId,
                error: recovery.triggerError,
                conflict: true,
                remoteState: "remote_deleted",
                restoreDeclined: true,
              };
            }
            recovery.approved = true;
            recovery.approvedOverlappingFields = preview.overlappingFields;
            deletedDraftRecoveriesRef.current.set(recoveryKey, recovery);
          }

          const restoreOutcome = await submitDeletedDraftRestore({
            deletedItem: recovery.deleted,
            token,
            actor: recovery.actor,
          });
          if (!restoreOutcome.restored) {
            const current = restoreOutcome.current;
            const sameDeletedTarget =
              isVersionConflictError(restoreOutcome.error) &&
              current?.is_deleted &&
              String(current.id) === recoveryKey;
            if (sameDeletedTarget) {
              recovery.deleted = current;
              recovery.approved = false;
              recovery.approvedOverlappingFields = [];
              deletedDraftRecoveriesRef.current.set(recoveryKey, recovery);
              continue;
            }

            if (
              isVersionConflictError(restoreOutcome.error) &&
              !current
            ) {
              deletedDraftRecoveriesRef.current.delete(recoveryKey);
              return {
                saved: false,
                item: null,
                pendingItem: candidate,
                authoritativeBase: recovery.base,
                mutationId: recovery.updateMutationId,
                error: restoreOutcome.error,
                conflict: true,
                remoteState: "remote_missing",
              };
            }

            deletedDraftRecoveriesRef.current.set(recoveryKey, recovery);
            return {
              saved: false,
              item: recovery.deleted,
              pendingItem: candidate,
              authoritativeBase: recovery.deleted,
              mutationId: recovery.updateMutationId,
              error: restoreOutcome.error,
              conflict: true,
              remoteState: "remote_deleted",
              restorePending: restoreOutcome.retryable,
              tagConflict: restoreOutcome.tagConflict,
            };
          }

          const restoredItem = restoreOutcome.item;
          const restoreMerge = rebaseDraftOntoRestoredItem({
            base: recovery.base,
            local: candidate,
            restored: restoredItem,
            fields,
          });
          const approvedOverlaps = new Set(
            recovery.approvedOverlappingFields || []
          );
          const newOverlaps = restoreMerge.overlappingFields.filter(
            (field) => !approvedOverlaps.has(field)
          );
          const rebasedCandidate = withCandidateAudit(restoreMerge.merged);

          deletedDraftRecoveriesRef.current.delete(recoveryKey);
          restoreMutationIntentsRef.current.delete(
            `draft-restore:${recoveryKey}`
          );
          if (newOverlaps.length > 0) {
            const keepLocalValues = window.confirm(
              `The restored item also changed in: ${newOverlaps.join(", ")}.\n\n` +
                "Select OK to keep your values for those fields. Select Cancel to keep the restored item and retain your draft for review."
            );
            if (!keepLocalValues) {
              return {
                saved: false,
                item: restoredItem,
                pendingItem: rebasedCandidate,
                authoritativeBase: restoredItem,
                mutationId: createMutationId(),
                error: recovery.triggerError,
                conflict: true,
                restoredBeforeSave: true,
              };
            }
          }

          candidate = rebasedCandidate;
          comparisonBase = restoredItem;
          currentMutationId = createMutationId();
          restoredBeforeSave = true;
          return null;
        }

        recovery.approved = false;
        deletedDraftRecoveriesRef.current.set(recoveryKey, recovery);
        return {
          saved: false,
          item: recovery.deleted,
          pendingItem: candidate,
          authoritativeBase: recovery.deleted,
          mutationId: recovery.updateMutationId,
          error: new Error("The deleted item kept changing while restore was retried."),
          conflict: true,
          remoteState: "remote_deleted",
        };
      };

      const existingRecovery = deletedDraftRecoveriesRef.current.get(recoveryKey);
      if (existingRecovery) {
        if (currentMutationId !== existingRecovery.updateMutationId) {
          existingRecovery.updateMutationId = currentMutationId;
        }
        const recoveryOutcome = await recoverDeletedDraft(existingRecovery);
        if (recoveryOutcome) return recoveryOutcome;
      }

      // A bounded retry handles a new server write that lands while an earlier
      // conflict is being resolved without creating an unbounded save loop.
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/equipment/update`,
            withConcurrencyMetadata(withoutRfidFields(candidate), currentMutationId),
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "X-Client": "web",
              },
            }
          );
          if (!response.data?.item) {
            const malformedResponseError = new Error(
              "The server did not return the authoritative equipment row."
            );
            malformedResponseError.response = response;
            throw malformedResponseError;
          }
          return {
            saved: true,
            item: response.data.item,
            replayed: isMutationReplay(response.data),
            restoredBeforeSave,
            mutationId: null,
            error: null,
          };
        } catch (error) {
          const conflictState = classifyUpdateConflict(error);
          if (
            conflictState?.kind === "remote_deleted" ||
            conflictState?.kind === "remote_missing"
          ) {
            if (conflictState.kind === "remote_deleted") {
              const recovery = {
                base: comparisonBase,
                deleted: conflictState.current,
                actor: actor || "",
                approved: false,
                approvedOverlappingFields: [],
                updateMutationId: createMutationId(),
                triggerError: error,
              };
              deletedDraftRecoveriesRef.current.set(recoveryKey, recovery);
              const recoveryOutcome = await recoverDeletedDraft(recovery);
              if (recoveryOutcome) return recoveryOutcome;
              continue;
            }
            deletedDraftRecoveriesRef.current.delete(recoveryKey);
            return {
              saved: false,
              item: conflictState.current,
              pendingItem: candidate,
              authoritativeBase: conflictState.current || comparisonBase,
              mutationId: createMutationId(),
              error,
              conflict: true,
              remoteState: conflictState.kind,
            };
          }

          if (conflictState?.kind !== "version_conflict") {
            const tagConflict = getEquipmentTagConflictDetails(error);
            return {
              saved: false,
              item: null,
              pendingItem: candidate,
              authoritativeBase: comparisonBase,
              mutationId: isRetryableMutationError(error)
                ? currentMutationId
                : null,
              error,
              conflict: Boolean(tagConflict),
              tagConflict,
            };
          }

          const serverItem = conflictState.current;
          const merge = mergeEquipmentConflict({
            base: comparisonBase,
            local: candidate,
            server: serverItem,
            fields,
          });
          const rebasedCandidate = withCandidateAudit(merge.merged);

          if (merge.overlappingFields.length > 0) {
            const keepLocalValues = window.confirm(
              `This item was changed by another user in: ${merge.overlappingFields.join(", ")}.\n\n` +
              "Select OK to keep your values for those fields and preserve their other changes. " +
              "Select Cancel to keep your edit unsaved so you can review it."
            );
            if (!keepLocalValues) {
              return {
                saved: false,
                item: serverItem,
                pendingItem: rebasedCandidate,
                authoritativeBase: serverItem,
                mutationId: createMutationId(),
                error,
                conflict: true,
              };
            }
          }

          candidate = rebasedCandidate;
          comparisonBase = serverItem;
          currentMutationId = createMutationId();
        }
      }

      return {
        saved: false,
        item: null,
        pendingItem: candidate,
        authoritativeBase: comparisonBase,
        mutationId: currentMutationId,
        conflict: true,
        error: new Error("The item kept changing while the conflict was being resolved."),
      };
    },
    [submitDeletedDraftRestore]
  );
  const isDeletedView = registerView === REGISTER_VIEW_DELETED;
  const isReadOnlyUser = user?.role === "viewer" || user?.role === "reporting";
  const gridColumns = useMemo(
    () =>
      allColumns.map((column) =>
        column.editable
          ? {
              ...column,
              editable: !isDeletedView && !isReadOnlyUser,
            }
          : column
      ),
    [allColumns, isDeletedView, isReadOnlyUser]
  );
  const areaOptions = useMemo(() => {
    const uniqueAreas = new Set(
      equipmentData.map((item) => item.area).filter(Boolean)
    );
    return Array.from(uniqueAreas).sort((a, b) => a.localeCompare(b));
  }, [equipmentData]);
  const tagOptions = useMemo(() => {
    const uniqueTags = new Set(
      equipmentData.map((item) => item.equipment_tag).filter(Boolean)
    );
    return Array.from(uniqueTags).sort((a, b) => a.localeCompare(b));
  }, [equipmentData]);
  const filteredCopyItems = useMemo(() => {
    const tagQuery = copySearchTag.trim().toLowerCase();
    const modelQuery = copySearchModel.trim().toLowerCase();
    return equipmentData.filter((item) => {
      const matchesTag = tagQuery
        ? String(item.equipment_tag || "").toLowerCase().includes(tagQuery)
        : true;
      const matchesModel = modelQuery
        ? String(item.model_no || "").toLowerCase().includes(modelQuery)
        : true;
      return matchesTag && matchesModel;
    });
  }, [equipmentData, copySearchTag, copySearchModel]);

  useEffect(() => {
    if (openAddDialog) {
      setCopySearchTag("");
      setCopySearchModel("");
      setSelectedCopyItem(null);
    }
  }, [openAddDialog]);
  const defaultVisibilityModel = useMemo(
    () => ({
      id: false,
      old_rfid: false,
      rfid_confirmed: false,
      serial_no: false,
      category_epl: false,
      ip_rating: false,
      certifying_authority: false,
      certificate_number: false,
      edit_date: false,
    }),
    []
  );
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("columnVisibility"));
      if (stored && typeof stored === "object") {
        return { ...defaultVisibilityModel, ...stored };
      }
      return defaultVisibilityModel;
    } catch {
      return defaultVisibilityModel;
    }
  });
  const hiddenEditableFieldCount = useMemo(
    () =>
      gridColumns.filter(
        (column) => column.editable && columnVisibilityModel[column.field] === false
      ).length,
    [gridColumns, columnVisibilityModel]
  );
  const handleColumnVisibilityChange = useCallback(
    (newModel) => {
      localStorage.setItem("columnVisibility", JSON.stringify(newModel));
      setColumnVisibilityModel(newModel);
    },
    []
  );
  const fetchEquipmentData = useCallback(async (status = registerView, options = {}) => {
    const { preserveSelection = false, silent = false } = options;
    try {
      const token = sessionToken;
      if (!token) {
        if (!silent) {
          toast.error("Unauthorized. Please log in.");
        }
        return false;
      }
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/equipment`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { status },
        }
      );
      if (token !== localStorage.getItem("token")) return false;
      const rows = Array.isArray(res.data) ? res.data : [];
      setEquipmentData(rows.map((row) => ({ ...row })));
      setOriginalData(rows.map((row) => ({ ...row })));
      if (!preserveSelection) {
        setSelectedRows([]);
      }
      return true;
    } catch (err) {
      console.error("Error fetching equipment data:", err);
      if (!silent) {
        toast.error("Failed to load equipment data.");
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [registerView, sessionToken]);

  const revokeObjectUrl = (url) => {
    if (url) {
      URL.revokeObjectURL(url);
    }
  };

  const fetchImageBlobUrl = async (imageUuid, { signal, token = sessionToken } = {}) => {
    if (!imageUuid) {
      return null;
    }

    const imageResponse = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/image/${imageUuid}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
        signal,
      }
    );
    return URL.createObjectURL(imageResponse.data);
  };

  const loadInspectPhoto = async (imageUuid) => {
    photoLoadAbortRef.current?.abort();
    const controller = new AbortController();
    photoLoadAbortRef.current = controller;
    const request = ++photoRequestRef.current;
    const token = sessionToken;
    setInspectPhotoError("");
    setInspectPhotoState(imageUuid ? "loading" : "idle");
    if (!imageUuid) return;
    try {
      const url = await fetchImageBlobUrl(imageUuid, { signal: controller.signal, token });
      if (controller.signal.aborted || request !== photoRequestRef.current || token !== localStorage.getItem("token")) {
        revokeObjectUrl(url); return;
      }
      setInspectExistingImageBlobUrl(url);
      setInspectPhotoState("idle");
    } catch (error) {
      if (controller.signal.aborted || request !== photoRequestRef.current || token !== localStorage.getItem("token")) return;
      setInspectPhotoState("unavailable");
      setInspectPhotoError(error.response?.status === 404 ? "The attached photo is unavailable. Your inspection can still be edited." : "Could not load the attached photo. Check your connection and retry.");
    }
  };

  const formatInspectionTimestamp = (value) => {
    if (!value) {
      return "N/A";
    }

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : "N/A";
  };

  useEffect(() => {
    if (!user) return;
    fetchEquipmentData(registerView);
  }, [user, fetchEquipmentData, registerView]);
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
    quickFilterExcludeHiddenColumns: false,
  });
  // Client-side dashboard filters (used to avoid DataGrid filterModel crashes on some operators)
  const [dashboardFilter, setDashboardFilter] = useState(null);
  useEffect(() => {
    if (!location.state || !location.state.filter) return;

    if (location.state.filter === "failed") {
      setDashboardFilter(null);
      setFilterModel((prev) => ({
        ...prev,
        items: [
          { field: "inspection_result", operator: "equals", value: "Fail" },
        ],
        quickFilterExcludeHiddenColumns: false,
      }));
      return;
    } else if (location.state.filter === "uninspected") {
      setDashboardFilter(null);
      setFilterModel((prev) => ({
        ...prev,
        items: [
          { field: "inspection_date", operator: "isEmpty", value: "" },
        ],
        quickFilterExcludeHiddenColumns: false,
      }));
      return;
    }

    // Dashboard: filter by area (location)
    if (location.state.filter === 'area') {
      const area = location.state.area;
      setDashboardFilter({ type: 'area', area });
      // Clear DataGrid filterModel to avoid combining filters
      setFilterModel((prev) => ({ ...prev, items: [] }));
      return;
    }

    // Dashboard: overdue 60 months (legacy state)
    if (location.state.filter === 'overdue60') {
      setDashboardFilter({
        type: 'inspectionDue',
        bucket: 'overdue',
        months: DEFAULT_INSPECTION_VALIDITY_MONTHS,
      });
      setFilterModel((prev) => ({ ...prev, items: [] }));
      return;
    }

    if (location.state.filter === 'inspectionDue') {
      const bucket = location.state.bucket;
      const months = Number.parseInt(location.state.months, 10) || DEFAULT_INSPECTION_VALIDITY_MONTHS;
      setDashboardFilter({ type: 'inspectionDue', bucket, months });
      setFilterModel((prev) => ({ ...prev, items: [] }));
      return;
    }
  }, [location.state]);
  const handleIndependentRefresh = useCallback(async () => {
    if (batchActionInProgress) {
      return;
    }
    if (hasPendingGridChanges) {
      toast.info(
        isEditingAnyRow
          ? "Finish or cancel the current row edit before refreshing."
          : "Save or discard your changes before refreshing."
      );
      return;
    }
    toast.info("Refreshing data...");
    const refreshed = await fetchEquipmentData(registerView);
    if (refreshed) {
      toast.success("Data refreshed successfully!");
    }
  }, [batchActionInProgress, fetchEquipmentData, hasPendingGridChanges, isEditingAnyRow, registerView]);

  const handleRegisterViewChange = useCallback(
    (nextView) => {
      if (!nextView || nextView === registerView) {
        return;
      }

      if (batchActionInProgress) {
        return;
      }

      if (hasPendingGridChanges) {
        const shouldDiscard = window.confirm(
          isEditingAnyRow
            ? "Switch views and discard the current row edit and all unsaved changes?"
            : "You have unsaved changes. Switch views and discard them?"
        );
        if (!shouldDiscard) {
          return;
        }
      }

      setEditedRows({});
      updateMutationIntentsRef.current.clear();
      Object.keys(editedRows).forEach((id) => {
        deletedDraftRecoveriesRef.current.delete(String(id));
        restoreMutationIntentsRef.current.delete(`draft-restore:${String(id)}`);
      });
      setRowModesModel((currentModes) =>
        Object.fromEntries(
          Object.entries(currentModes).map(([id]) => [
            id,
            { mode: "view", ignoreModifications: true },
          ])
        )
      );
      setSelectedRows([]);
      if (nextView === REGISTER_VIEW_DELETED) {
        setDashboardFilter(null);
        setFilterModel((prev) => ({
          ...prev,
          items: [],
        }));
      }
      setRegisterView(nextView);
    },
    [batchActionInProgress, editedRows, hasPendingGridChanges, isEditingAnyRow, registerView]
  );

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({ hiddenEditableFieldCount, isEditingAnyRow }) => (
      <GridToolbarContainer
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector />
          <GridToolbarExport />
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleIndependentRefresh}
            disabled={batchActionInProgress || hasPendingGridChanges}
            title={
              hasPendingGridChanges
                ? "Save or discard changes before refreshing"
                : "Refresh register data"
            }
            size="small"
            sx={{
              textTransform: "none",
              color: "var(--brand-primary, #1976d2)",
              fontWeight: 500,
              fontSize: 12,
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            REFRESH
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
            ml: "auto",
            flexWrap: "wrap",
          }}
        >
          <Stack direction="row" spacing={1}>
            <Button
              variant={!isDeletedView ? "contained" : "outlined"}
              size="small"
              onClick={() => handleRegisterViewChange(REGISTER_VIEW_ACTIVE)}
              disabled={batchActionInProgress}
              sx={{ textTransform: "none" }}
            >
              Active Items
            </Button>
            <Button
              variant={isDeletedView ? "contained" : "outlined"}
              size="small"
              color={isDeletedView ? "warning" : "inherit"}
              onClick={() => handleRegisterViewChange(REGISTER_VIEW_DELETED)}
              disabled={batchActionInProgress}
              sx={{ textTransform: "none" }}
            >
              Deleted Items
            </Button>
          </Stack>
          <Typography
            variant="caption"
            role="status"
            sx={[
              { color: "text.secondary", borderRadius: 0.75 },
              isEditingAnyRow && hiddenEditableFieldCount > 0 && {
                color: "#713f12",
                backgroundColor: "#fff3b0",
                boxShadow: `0 0 0 1px ${alpha("#f59e0b", 0.55)}, 0 0 12px 4px ${alpha("#f59e0b", 0.35)}`,
              },
            ]}
          >
            {isDeletedView
              ? "Deleted rows are read-only. Restore returns them to the active register."
              : hiddenEditableFieldCount > 0
              ? `${hiddenEditableFieldCount} editable ${hiddenEditableFieldCount === 1 ? "field is" : "fields are"} hidden. Show ${hiddenEditableFieldCount === 1 ? "it" : "them"} in Columns to edit.`
              : "Showing active register items."}
          </Typography>
          <GridToolbarQuickFilter debounceMs={300} sx={{ ml: { sm: 1 } }} />
        </Box>
      </GridToolbarContainer>
    );
    return Toolbar;
  }, [
    batchActionInProgress,
    handleIndependentRefresh,
    handleRegisterViewChange,
    hasPendingGridChanges,
    isDeletedView,
  ]);
  const processRowUpdate = (newRow, oldRow) => {
    const hasSessionChanges = editableFieldNames.some(
      (field) =>
        normalizeEditableValue(newRow[field]) !==
        normalizeEditableValue(oldRow?.[field])
    );

    if (!hasSessionChanges) {
      return oldRow;
    }

    const baselineRow = originalRowsById.get(String(newRow.id)) || oldRow;
    const hasNetChanges = editableFieldNames.some(
      (field) =>
        normalizeEditableValue(newRow[field]) !==
        normalizeEditableValue(baselineRow?.[field])
    );

    if (!hasNetChanges) {
      updateMutationIntentsRef.current.delete(String(newRow.id));
      deletedDraftRecoveriesRef.current.delete(String(newRow.id));
      restoreMutationIntentsRef.current.delete(
        `draft-restore:${String(newRow.id)}`
      );
      setEditedRows((prev) => {
        if (!prev[newRow.id]) {
          return prev;
        }
        const nextEdits = { ...prev };
        delete nextEdits[newRow.id];
        return nextEdits;
      });
      return baselineRow || oldRow;
    }

    const updatedRow = {
      ...newRow,
      last_useredit: `${user?.name || ""} ${user?.surname || ""}`.trim(),
      edit_date: new Date().toISOString(),
    };
    setEditedRows((prev) => ({ ...prev, [updatedRow.id]: updatedRow }));
    return updatedRow;
  };
  const onProcessRowUpdateError = (err) => {
    console.error("Row update error:", err);
    toast.error("Error editing row. Please try again.");
  };
  const handleBatchSave = useCallback(async () => {
    if (
      batchActionInFlightRef.current ||
      isBatchSaving ||
      isDiscardingChanges
    ) {
      return;
    }

    const rowsToUpdate = Object.values(editedRows);
    if (!rowsToUpdate.length) {
      toast.info("No changes to save.");
      return;
    }

    batchActionInFlightRef.current = true;
    setIsBatchSaving(true);
    try {
      const token = sessionToken;
      const actor = `${user?.name || ""} ${user?.surname || ""}`.trim();
      const results = await Promise.all(
        rowsToUpdate.map(async (row) => {
          const intentKey = String(row.id);
          const intent = getOrCreateMutationIntent({
            registry: updateMutationIntentsRef.current,
            key: intentKey,
            input: row,
            buildPayload: () => row,
          });
          const baseRow = originalRowsById.get(intentKey) || row;
          const outcome = await submitEquipmentUpdate({
            row: intent.payload,
            baseRow,
            fields: editableFieldNames,
            token,
            mutationId: intent.mutationId,
            actor,
            draftLabel: "changes",
          });
          if (outcome.saved) {
            updateMutationIntentsRef.current.delete(intentKey);
          } else if (outcome.mutationId) {
            rememberMutationIntent({
              registry: updateMutationIntentsRef.current,
              key: intentKey,
              input: outcome.pendingItem || intent.payload,
              payload: outcome.pendingItem || intent.payload,
              mutationId: outcome.mutationId,
            });
          } else {
            updateMutationIntentsRef.current.delete(intentKey);
          }
          return {
            row,
            pendingRow: outcome.pendingItem || row,
            canonicalRow: outcome.item,
            authoritativeBase: outcome.authoritativeBase || baseRow,
            baseRow,
            saved: outcome.saved,
            replayed: outcome.replayed === true,
            conflict: outcome.conflict === true,
            remoteState: outcome.remoteState,
            restoreDeclined: outcome.restoreDeclined === true,
            restorePending: outcome.restorePending === true,
            restoredBeforeSave: outcome.restoredBeforeSave === true,
            tagConflict: outcome.tagConflict,
            error: outcome.error,
          };
        })
      );

      const savedResults = results.filter((result) => result.saved);
      const failedResults = results.filter((result) => !result.saved);
      const failedEdits = failedResults.reduce((accumulator, result) => {
        accumulator[result.row.id] = result.pendingRow;
        return accumulator;
      }, {});
      const unresolvedRows = failedResults.map((result) => ({
        id: result.row.id,
        row: result.pendingRow,
      }));
      const unresolvedBases = failedResults.map((result) => ({
        id: result.row.id,
        row: result.authoritativeBase || result.baseRow,
      }));

      const refreshed = savedResults.length
        ? await fetchEquipmentData(registerView, {
            preserveSelection: true,
            silent: true,
          })
        : false;
      if (failedResults.length) {
        // A successful refresh may omit a concurrently deleted row. Keep every
        // unresolved local edit visible and retain the exact base used for its
        // next conflict comparison.
        setEquipmentData((currentRows) =>
          preserveRowsById(currentRows, unresolvedRows)
        );
        setOriginalData((currentRows) =>
          preserveRowsById(currentRows, unresolvedBases)
        );
      }
      if (!refreshed && savedResults.length) {
        const savedRowsById = new Map(
          savedResults.map((result) => [
            String(result.row.id),
            result.canonicalRow,
          ])
        );
        setEquipmentData((currentRows) =>
          currentRows.map(
            (row) => savedRowsById.get(String(row.id)) || row
          )
        );
        setOriginalData((currentRows) =>
          currentRows.map(
            (row) => savedRowsById.get(String(row.id)) || row
          )
        );
      }

      setEditedRows(failedEdits);
      setRowModesModel({});

      const tagConflictResults = failedResults.filter(
        (result) => result.tagConflict
      );
      const firstTagConflict = tagConflictResults[0]?.tagConflict;
      const conflictingItemLabel = firstTagConflict
        ? [
            firstTagConflict.conflictingTag
              ? `tag ${firstTagConflict.conflictingTag}`
              : "unknown tag",
            firstTagConflict.conflictingItem?.id
              ? `item ${firstTagConflict.conflictingItem.id}`
              : null,
          ]
            .filter(Boolean)
            .join(", ")
        : null;
      const tagConflictMessage = firstTagConflict
        ? `${firstTagConflict.message} Conflicting active ${conflictingItemLabel}. ` +
          "Change that active item's tag, then select Save again; your draft is retained."
        : null;

      if (!failedResults.length) {
        const rowLabel = savedResults.length === 1 ? "row" : "rows";
        const restoredCount = savedResults.filter(
          (result) => result.restoredBeforeSave
        ).length;
        if (refreshed) {
          toast.success(
            `${savedResults.length} changed ${rowLabel} saved${
              restoredCount
                ? `; ${restoredCount} deleted ${restoredCount === 1 ? "item was" : "items were"} restored first`
                : ""
            }.`
          );
        } else {
          toast.warning(
            `${savedResults.length} changed ${rowLabel} saved, but the register could not be refreshed.`
          );
        }
      } else if (savedResults.length) {
        toast.warning(
          `${savedResults.length} saved; ${failedResults.length} remain unsaved${
            refreshed ? "." : ", and the register could not be refreshed."
          }${tagConflictMessage ? ` ${tagConflictMessage}` : ""}`
        );
      } else {
        const conflictFailures = failedResults.filter(
          (result) => result.conflict
        ).length;
        const remoteDeletedFailures = failedResults.filter(
          (result) => result.remoteState === "remote_deleted"
        ).length;
        const remoteMissingFailures = failedResults.filter(
          (result) => result.remoteState === "remote_missing"
        ).length;
        const permissionFailures = failedResults.filter(
          (result) => result.error?.response?.status === 403
        ).length;
        const declinedRestoreFailures = failedResults.filter(
          (result) => result.restoreDeclined
        ).length;
        const pendingRestoreFailures = failedResults.filter(
          (result) => result.restorePending
        ).length;
        toast.error(
          tagConflictMessage
            ? tagConflictMessage
            : declinedRestoreFailures > 0
            ? `${declinedRestoreFailures} deleted item${declinedRestoreFailures === 1 ? " remains" : "s remain"} unsaved. Select Save again to choose Restore and retry; your local edits are retained.`
            : pendingRestoreFailures > 0
            ? `${pendingRestoreFailures} restore request${pendingRestoreFailures === 1 ? " could" : "s could"} not be confirmed after a temporary error. Select Save again to retry the exact request; your local edits are retained.`
            : remoteDeletedFailures > 0
            ? `${remoteDeletedFailures} item${remoteDeletedFailures === 1 ? " remains" : "s remain"} deleted elsewhere. Select Save again to restore and retry; your local edits are retained.`
            : remoteMissingFailures > 0
            ? `${remoteMissingFailures} item${remoteMissingFailures === 1 ? " no longer exists" : "s no longer exist"} on the server. Your local edits remain available for review.`
            : conflictFailures > 0
            ? `${conflictFailures} item conflict${conflictFailures === 1 ? "" : "s"} remain unsaved for review.`
            : permissionFailures === failedResults.length
            ? "You do not have permission to save these changes."
            : "Changes could not be saved. Your edits are still available."
        );
      }
    } catch (err) {
      console.error("Batch update error:", err);
      toast.error("Changes could not be saved. Your edits are still available.");
    } finally {
      setIsBatchSaving(false);
      batchActionInFlightRef.current = false;
      window.requestAnimationFrame(() => registerStatusRef.current?.focus());
    }
  }, [
    sessionToken,
    editableFieldNames,
    editedRows,
    fetchEquipmentData,
    isBatchSaving,
    isDiscardingChanges,
    originalRowsById,
    registerView,
    submitEquipmentUpdate,
    user,
  ]);
  const handleBatchCancel = useCallback(async () => {
    if (
      batchActionInFlightRef.current ||
      isBatchSaving ||
      isDiscardingChanges
    ) {
      return;
    }

    const discardedRowCount = Object.keys(editedRows).length;
    batchActionInFlightRef.current = true;
    setIsDiscardingChanges(true);

    // Revert locally immediately, then refresh from server to guarantee latest state.
    setEquipmentData(originalData.map((row) => ({ ...row })));
    updateMutationIntentsRef.current.clear();
    Object.keys(editedRows).forEach((id) => {
      deletedDraftRecoveriesRef.current.delete(String(id));
      restoreMutationIntentsRef.current.delete(`draft-restore:${String(id)}`);
    });
    setRowModesModel({});

    try {
      const refreshed = await fetchEquipmentData(registerView, {
        preserveSelection: true,
        silent: true,
      });
      setEditedRows({});
      if (refreshed) {
        const rowLabel = discardedRowCount === 1 ? "row" : "rows";
        toast.info(`${discardedRowCount} unsaved ${rowLabel} discarded.`);
      } else {
        toast.warning(
          "Changes were discarded locally, but the register could not be refreshed."
        );
      }
    } finally {
      setIsDiscardingChanges(false);
      setDiscardChangesDialogOpen(false);
      batchActionInFlightRef.current = false;
      window.requestAnimationFrame(() => registerStatusRef.current?.focus());
    }
  }, [editedRows, fetchEquipmentData, isBatchSaving, isDiscardingChanges, originalData, registerView]);
  const cancelRowEdits = useCallback(
    (rowIds) => {
      if (!Array.isArray(rowIds) || !rowIds.length) {
        return;
      }
      setRowModesModel((prev) => {
        const next = { ...prev };
        rowIds.forEach((id) => {
          next[id] = { mode: "view", ignoreModifications: true };
        });
        return next;
      });
    },
    []
  );

  useEffect(() => {
    if (!hasPendingPageChanges) {
      return undefined;
    }
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasPendingPageChanges]);

  useEffect(() => {
    const handleShortcutKeyDown = (event) => {
      if (event.defaultPrevented) {
        return;
      }

      const eventTarget = event.target;
      const isInsideDialog = eventTarget?.closest?.('[role="dialog"]');
      if (isInsideDialog) {
        return;
      }

      const key = event.key?.toLowerCase();

      if ((event.ctrlKey || event.metaKey) && key === "s") {
        if (!hasPendingGridChanges) {
          return;
        }
        event.preventDefault();
        if (isEditingAnyRow) {
          toast.info("Finish editing the current row before saving.");
          return;
        }
        if (batchActionInProgress) {
          return;
        }
        handleBatchSave();
        return;
      }

      if (event.key === "Escape") {
        if (!eventTarget?.closest?.('.MuiDataGrid-root')) {
          return;
        }
        const editingRowIds = Object.entries(rowModesModel)
          .filter(([, modeState]) => modeState?.mode === "edit")
          .map(([id]) => id);

        if (!editingRowIds.length) {
          return;
        }
        event.preventDefault();
        cancelRowEdits(editingRowIds);
      }
    };

    window.addEventListener("keydown", handleShortcutKeyDown);
    return () => {
      window.removeEventListener("keydown", handleShortcutKeyDown);
    };
  }, [
    batchActionInProgress,
    cancelRowEdits,
    handleBatchSave,
    hasPendingGridChanges,
    isEditingAnyRow,
    rowModesModel,
  ]);

  const loadItemHistory = async (item) => {
    const requestId = ++historyRequestRef.current;
    setHistoryItem(item);
    setHistoryRows([]);
    setHistoryError("");
    setHistoryLoading(true);
    setHistoryDialogOpen(true);
    try {
      const token = sessionToken;
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/equipment/${item.id}/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!Array.isArray(res.data)) throw new Error("Invalid history response");
      if (requestId === historyRequestRef.current) setHistoryRows(res.data);
    } catch (err) {
      if (requestId !== historyRequestRef.current) return;
      console.error("Error loading history:", err);
      setHistoryError("Item history could not be loaded. Please try again.");
    } finally {
      if (requestId === historyRequestRef.current) setHistoryLoading(false);
    }
  };

  const handleOpenHistory = () => {
    if (!selectedRows || selectedRows.length !== 1) {
      toast.info("Please select exactly one item to view history.");
      return;
    }
    const equipmentId = selectedRows[0];
    loadItemHistory(equipmentData.find(item => item.id === equipmentId) || { id: equipmentId });
  };

  const handleCloseHistory = () => {
    historyRequestRef.current += 1;
    setHistoryDialogOpen(false);
    setHistoryLoading(false);
    setHistoryRows([]);
    setHistoryError("");
  };
  const handleCopyItemSelection = useCallback(
    (item) => {
      if (!item) {
        return;
      }
      const copiedValues = editableFieldNames.reduce((acc, field) => {
        acc[field] = item[field] ?? "";
        return acc;
      }, {});
      copiedValues.area = "";
      copiedValues.equipment_tag = "";
      setNewItemValues(copiedValues);
    },
    [editableFieldNames]
  );
  const handleNewItemChange = (e) => {
    const { name, value } = e.target;
    setNewItemValues((prev) => ({ ...prev, [name]: value }));
  };
  const handleAddItem = async () => {
    try {
      const token = sessionToken;
      if (!token) {
        toast.error("Unauthorized. Please log in.");
        return;
      }
      const actor = `${user?.name || ""} ${user?.surname || ""}`.trim();
      const input = {
        ...newItemValues,
        last_useredit: actor,
      };
      const intent = getOrCreateMutationIntent({
        registry: addMutationIntentsRef.current,
        key: "add",
        input,
        buildPayload: (mutationId) => {
          const newItem = {
            ...input,
            edit_date: new Date().toISOString(),
            mutation_id: mutationId,
          };
          if (!newItemValues.id) delete newItem.id;
          return newItem;
        },
      });
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/equipment/add`,
        intent.payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Client": "web",
          },
        }
      );
      if (res.status === 200 || res.status === 201) {
        if (!isMutationReplay(res.data) && !res.data?.item) {
          throw Object.assign(
            new Error("The server did not return the created equipment row."),
            { response: res }
          );
        }
        addMutationIntentsRef.current.delete("add");
        toast.success("Item added successfully!");
        if (isMutationReplay(res.data)) {
          const refreshed = await fetchEquipmentData(registerView, {
            preserveSelection: true,
            silent: true,
          });
          if (!refreshed) {
            toast.warning(
              "The retry was accepted, but the register could not be refreshed. Refresh before editing the new item."
            );
          }
        } else if (res.data?.item) {
          setEquipmentData((prev) => [...prev, res.data.item]);
          setOriginalData((prev) => [...prev, res.data.item]);
        }
        setOpenAddDialog(false);
        setNewItemValues({});
      }
    } catch (error) {
      console.error("Error adding item:", error);
      clearMutationIntentAfterError(
        addMutationIntentsRef.current,
        "add",
        error
      );
      const backendMessage =
        error.response?.data?.message || "Error adding item.";
      toast.error(backendMessage);
    }
  };
  const handleDeleteClick = () => {
    if (!selectedRows.length) {
      toast.warn("Please select at least one row to delete.");
      return;
    }
    setShowDeleteConfirm(true);
  };
  const handleDeleteConfirmed = async () => {
    try {
      const token = sessionToken;
      const lastUserEdit = `${user?.name || ""} ${user?.surname || ""}`.trim();
      const rowsById = new Map(
        equipmentData.map((row) => [String(row.id), row])
      );
      const results = await Promise.all(
        selectedRows.map(async (id) => {
          const intentKey = String(id);
          const row = rowsById.get(String(id));
          const intent = getOrCreateMutationIntent({
            registry: deleteMutationIntentsRef.current,
            key: intentKey,
            input: {
              id,
              expected_version: row?.version,
              last_useredit: lastUserEdit,
            },
            buildPayload: (mutationId) => ({
              expected_version: row?.version,
              mutation_id: mutationId,
              last_useredit: lastUserEdit,
              edit_date: new Date().toISOString(),
            }),
          });
          try {
            const response = await axios.delete(
              `${process.env.REACT_APP_API_BASE_URL}/api/equipment/${id}/delete`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "X-Client": "web",
                },
                params: intent.payload,
              }
            );
            deleteMutationIntentsRef.current.delete(intentKey);
            return {
              id,
              succeeded: true,
              item: response.data?.item,
              replayed: isMutationReplay(response.data),
            };
          } catch (error) {
            const current = error.response?.data?.current;
            // Two users deleting the same item converge on the requested state.
            if (isVersionConflictError(error) && current?.is_deleted) {
              deleteMutationIntentsRef.current.delete(intentKey);
              return { id, succeeded: true, item: current, converged: true };
            }
            clearMutationIntentAfterError(
              deleteMutationIntentsRef.current,
              intentKey,
              error
            );
            return {
              id,
              succeeded: false,
              item: current,
              retryable: isRetryableMutationError(error),
              error,
            };
          }
        })
      );

      const succeededIds = new Set(
        results.filter((result) => result.succeeded).map((result) => String(result.id))
      );
      const failed = results.filter((result) => !result.succeeded);
      const latestFailedRows = new Map(
        failed.filter((result) => result.item).map((result) => [String(result.id), result.item])
      );
      setEquipmentData((previous) =>
        previous
          .filter((row) => !succeededIds.has(String(row.id)))
          .map((row) => latestFailedRows.get(String(row.id)) || row)
      );
      setOriginalData((previous) =>
        previous
          .filter((row) => !succeededIds.has(String(row.id)))
          .map((row) => latestFailedRows.get(String(row.id)) || row)
      );
      setSelectedRows(failed.map((result) => result.id));
      if (results.some((result) => result.succeeded && result.replayed)) {
        const refreshed = await fetchEquipmentData(registerView, {
          preserveSelection: true,
          silent: true,
        });
        if (!refreshed) {
          toast.warning(
            "The delete retry was accepted, but the latest register state could not be refreshed."
          );
        }
      }
      if (failed.length === 0) {
        toast.success("Selected rows marked as deleted successfully!");
      } else {
        toast.warning(
          `${succeededIds.size} deleted; ${failed.length} changed elsewhere and were not deleted.`
        );
      }
    } catch (err) {
      console.error("Error deleting rows:", err);
      toast.error("Error deleting rows.");
    } finally {
      setShowDeleteConfirm(false);
    }
  };
  const handleRestoreConfirmed = async () => {
    try {
      const token = sessionToken;
      const lastUserEdit = `${user?.name || ""} ${user?.surname || ""}`.trim();
      const rowsById = new Map(
        equipmentData.map((row) => [String(row.id), row])
      );
      const results = await Promise.all(
        selectedRows.map(async (id) => {
          const intentKey = String(id);
          const row = rowsById.get(String(id));
          const intent = getOrCreateMutationIntent({
            registry: restoreMutationIntentsRef.current,
            key: intentKey,
            input: {
              id,
              expected_version: row?.version,
              last_useredit: lastUserEdit,
            },
            buildPayload: (mutationId) => ({
              last_useredit: lastUserEdit,
              edit_date: new Date().toISOString(),
              expected_version: row?.version,
              mutation_id: mutationId,
            }),
          });
          try {
            const response = await axios.post(
              `${process.env.REACT_APP_API_BASE_URL}/api/equipment/${id}/restore`,
              intent.payload,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "X-Client": "web",
                },
              }
            );
            restoreMutationIntentsRef.current.delete(intentKey);
            return {
              id,
              row,
              succeeded: true,
              item: response.data?.item,
              replayed: isMutationReplay(response.data),
            };
          } catch (error) {
            const current = error.response?.data?.current;
            // Two restores converge when the authoritative item is already active.
            const convergedItem = getRestoreConvergenceItem(error, id);
            if (convergedItem) {
              restoreMutationIntentsRef.current.delete(intentKey);
              return {
                id,
                row,
                succeeded: true,
                item: convergedItem,
                converged: true,
              };
            }
            clearMutationIntentAfterError(
              restoreMutationIntentsRef.current,
              intentKey,
              error
            );
            return {
              id,
              row,
              succeeded: false,
              item: current,
              retryable: isRetryableMutationError(error),
              tagConflict: getEquipmentTagConflictDetails(error),
              error,
            };
          }
        })
      );
      const failed = results.filter((result) => !result.succeeded);
      const refreshed = await fetchEquipmentData(REGISTER_VIEW_DELETED, {
        preserveSelection: true,
        silent: true,
      });
      if (!refreshed) {
        const succeededIds = new Set(
          results
            .filter((result) => result.succeeded)
            .map((result) => String(result.id))
        );
        setEquipmentData((rows) =>
          rows.filter((row) => !succeededIds.has(String(row.id)))
        );
        setOriginalData((rows) =>
          rows.filter((row) => !succeededIds.has(String(row.id)))
        );
      }
      const retryableRows = failed
        .filter((result) => result.retryable && result.row)
        .map((result) => ({ id: result.id, row: result.row }));
      if (retryableRows.length) {
        setEquipmentData((rows) => preserveRowsById(rows, retryableRows));
        setOriginalData((rows) => preserveRowsById(rows, retryableRows));
      }
      setSelectedRows(failed.map((result) => result.id));
      if (failed.length === 0) {
        toast.success(
          refreshed
            ? "Selected rows restored successfully!"
            : "Selected rows restored, but the deleted register could not be refreshed."
        );
      } else {
        const tagConflictFailures = failed.filter(
          (result) => result.tagConflict
        );
        if (tagConflictFailures.length) {
          const details = tagConflictFailures[0].tagConflict;
          const conflictingItem = details.conflictingItem;
          toast.warning(
            `${results.length - failed.length} restored; ${failed.length} not restored. ` +
              `${details.message} Conflicting active tag ${
                details.conflictingTag || "is unknown"
              }${conflictingItem?.id ? ` (item ${conflictingItem.id})` : ""}. ` +
              "Change that active item's tag, then retry Restore."
          );
        } else {
          toast.warning(
            `${results.length - failed.length} restored; ${failed.length} changed elsewhere and were not restored.`
          );
        }
      }
    } catch (err) {
      console.error("Error restoring rows:", err);
      toast.error(
        err.response?.data?.message || "Error restoring selected rows."
      );
    } finally {
      setShowRestoreConfirm(false);
    }
  };
  const buildChecklistAnswerRows = useCallback((questions = [], previousAnswers = []) => {
    const answerMap = new Map(
      (Array.isArray(previousAnswers) ? previousAnswers : [])
        .filter((entry) => entry && typeof entry.question === "string")
        .map((entry) => [entry.question, entry.answer || ""])
    );
    return (Array.isArray(questions) ? questions : []).map((question) => ({
      question,
      answer: answerMap.get(question) || "",
    }));
  }, []);

  const handleChecklistAnswerChange = (question, answer) => {
    setInspectionChecklistAnswers((prev) => {
      const next = prev.map((entry) => {
        if (entry.question !== question) return entry;
        return {
          ...entry,
          answer: entry.answer === answer ? "" : answer,
        };
      });

      const hasChecklistFail = next.some((entry) => entry.answer === "Fail");
      if (hasChecklistFail) {
        setCurrentItem((prevItem) =>
          prevItem ? { ...prevItem, inspection_result: "Fail" } : prevItem
        );
      }
      return next;
    });
  };

  const markAllChecklistAnswersPassed = () => {
    setInspectionChecklistAnswers((previous) =>
      previous.map((entry) =>
        entry.answer === "Pass" ? entry : { ...entry, answer: "Pass" }
      )
    );
  };

  const applyPassAllChecklist = () => {
    markAllChecklistAnswersPassed();
    setPassAllChecklistDialogOpen(false);
    if (currentItem?.inspection_result === "Fail") {
      toast.info(
        "All checklist questions are marked Pass. The overall inspection result remains Fail; review it before submitting."
      );
    } else {
      toast.success(
        `${inspectionChecklistAnswers.length} checklist ${
          inspectionChecklistAnswers.length === 1 ? "question" : "questions"
        } marked Pass.`
      );
    }
  };

  const handlePassAllChecklist = () => {
    if (
      checklistConfigLoading ||
      isSaving ||
      inspectionChecklistAnswers.length === 0
    ) {
      return;
    }

    const replacesExistingException = inspectionChecklistAnswers.some(
      (entry) => entry.answer === "Fail" || entry.answer === "NA"
    );
    if (replacesExistingException) {
      setPassAllChecklistDialogOpen(true);
      return;
    }

    applyPassAllChecklist();
  };

  const loadInspectionChecklist = async (item, request) => {
    const token = sessionToken;
    const checklist = await fetchChecklistConfig();
    if (!checklist || request !== inspectionOpenRequestRef.current || token !== localStorage.getItem("token")) return;
    const questions = checklist.checklistEnabled ? checklist.questions || [] : [];
    const baselineAnswers = buildChecklistAnswerRows(questions, item.checklist_answers || []);
    inspectionDraftBaselineRef.current = createInspectionDraftSnapshot(item, baselineAnswers, null);
    setInspectionChecklistAnswers(previous => buildChecklistAnswerRows(
      questions, previous.length ? previous : item.checklist_answers || []
    ));
  };

  const handleInspectClick = (id) => {
    const item = equipmentData.find((row) => row.id === id);
    if (item) {
      const request = ++inspectionOpenRequestRef.current;
      photoPreparationAbortRef.current?.abort();
      revokeObjectUrl(inspectImagePreviewUrl);
      revokeObjectUrl(inspectExistingImageBlobUrl);
      setInspectImageFile(null);
      setInspectImagePreviewUrl(null);
      setInspectExistingImageBlobUrl(null);

      const initialChecklistAnswers = [];
      inspectionSourceItemRef.current = item;
      inspectionDraftBaselineRef.current = createInspectionDraftSnapshot(
        item,
        initialChecklistAnswers,
        null
      );
      setInspectionChecklistAnswers(initialChecklistAnswers);
      setCurrentItem(item);
      setDiscardInspectionDialogOpen(false);
      setOpenInspectDialog(true);
      void loadInspectPhoto(item.image_uuid);
      void loadInspectionChecklist(item, request);
    }
  };
  const closeInspectDialog = () => {
    inspectionOpenRequestRef.current += 1;
    checklistRequestRef.current += 1;
    checklistAbortRef.current?.abort();
    setChecklistConfigLoading(false);
    photoRequestRef.current += 1;
    photoLoadAbortRef.current?.abort();
    photoPreparationAbortRef.current?.abort();
    setInspectPhotoState("idle");
    setInspectPhotoError("");
    setInspectUploadProgress(null);
    if (currentItem?.id !== undefined) {
      const itemId = String(currentItem.id);
      inspectionMutationIntentsRef.current.delete(itemId);
      deletedDraftRecoveriesRef.current.delete(itemId);
      restoreMutationIntentsRef.current.delete(`draft-restore:${itemId}`);
    }
    setOpenInspectDialog(false);
    setPassAllChecklistDialogOpen(false);
    setDiscardInspectionDialogOpen(false);
    setZoomDialogOpen(false);
    setZoomedImageUrl(null);
    revokeObjectUrl(inspectImagePreviewUrl);
    revokeObjectUrl(inspectExistingImageBlobUrl);
    setInspectImageFile(null);
    setInspectImagePreviewUrl(null);
    setInspectExistingImageBlobUrl(null);
    setInspectionChecklistAnswers([]);
    setCurrentItem(null);
    inspectionDraftBaselineRef.current = null;
    inspectionSourceItemRef.current = null;
  };
  const handleCloseInspectDialog = () => {
    if (isSaving) return;
    if (hasInspectionDraftChanges) {
      setDiscardInspectionDialogOpen(true);
      return;
    }
    closeInspectDialog();
  };
  const handleInspectChange = (e) => {
    if (!currentItem) return;
    const { name, value } = e.target;
    const updated = { ...currentItem, [name]: value };
    if (name === "inspection_result" && value === "Pass") {
      const hasChecklistFail = inspectionChecklistAnswers.some(
        (entry) => entry.answer === "Fail"
      );
      if (hasChecklistFail) {
        toast.error(
          "Inspection Result cannot be Pass while checklist contains a Fail answer."
        );
        updated.inspection_result = "Fail";
      }
    }
    if (name === "inspection_result" || name === "inspection_type") {
      updated.inspector = `${user?.name || ""} ${user?.surname || ""}`;
    }
    setCurrentItem(updated);
  };

  // Helpers for image selection in inspect dialog
  const handleAttachImageClick = () => {
    if (!fileInputRef.current) return;
    // Reset file input so selecting same file again still triggers change
    fileInputRef.current.value = "";
    fileInputRef.current.click();
  };

  const handleInspectImageChange = async (event) => {
    const source = event.target.files?.[0];
    if (!source || isSaving) return;
    photoPreparationAbortRef.current?.abort();
    photoLoadAbortRef.current?.abort();
    const controller = new AbortController();
    photoPreparationAbortRef.current = controller;
    const request = ++photoRequestRef.current;
    const token = sessionToken;
    setInspectPhotoState("preparing");
    setInspectPhotoError("");
    try {
      const prepared = await prepareInspectionImage(source, { signal: controller.signal });
      if (controller.signal.aborted || request !== photoRequestRef.current || token !== localStorage.getItem("token")) return;
      const selection = Object.freeze({ ...selectInspectionImage(currentItem?.image_uuid, prepared.file), ...prepared });
      const previewUrl = URL.createObjectURL(selection.file);
      revokeObjectUrl(inspectImagePreviewUrl);
      setInspectImageFile(selection);
      setInspectImagePreviewUrl(previewUrl);
      setCurrentItem(prev => prev ? { ...prev, image_uuid: selection.uuid } : prev);
      setInspectPhotoState("ready");
    } catch (error) {
      if (controller.signal.aborted || request !== photoRequestRef.current || token !== localStorage.getItem("token")) return;
      setInspectPhotoState("error");
      setInspectPhotoError(`${error.message || "Could not prepare this photo."} Your previous photo has been kept.`);
    }
  };

  const handleRemoveInspectImage = () => {
    if (!currentItem || isSaving) return;
    photoRequestRef.current += 1;
    photoPreparationAbortRef.current?.abort();
    photoLoadAbortRef.current?.abort();
    setInspectPhotoState("idle");
    setInspectPhotoError("");

    revokeObjectUrl(inspectImagePreviewUrl);
    revokeObjectUrl(inspectExistingImageBlobUrl);
    setInspectImageFile(null);
    setInspectImagePreviewUrl(null);
    setInspectExistingImageBlobUrl(null);
    setCurrentItem((prev) =>
      prev
        ? {
            ...prev,
            image_uuid: null,
          }
        : prev
    );

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmitInspect = async () => {
    if (!currentItem || isSaving || inspectPhotoState === "preparing" || checklistConfigLoading || checklistConfigError) return;
    if (!currentItem.inspection_type) {
      toast.error("Please select an Inspection Type.");
      return;
    }
    if (!currentItem.inspection_result) {
      toast.error("Please select an Inspection Result.");
      return;
    }

    const checklistEnabled = checklistConfig.checklistEnabled;
    const checklistQuestions = checklistEnabled
      ? checklistConfig.questions || []
      : [];
    if (checklistEnabled && checklistQuestions.length === 0) {
      toast.error(
        "Checklist is enabled but no checklist questions are configured for this database."
      );
      return;
    }

    if (checklistEnabled) {
      const allChecklistAnswersProvided = inspectionChecklistAnswers.length === checklistQuestions.length && inspectionChecklistAnswers.every((entry) =>
        CHECKLIST_ANSWER_OPTIONS.includes(entry.answer)
      );
      if (!allChecklistAnswersProvided) {
        toast.error("Please answer all checklist questions (Pass, Fail, or NA).");
        return;
      }

      const hasChecklistFail = inspectionChecklistAnswers.some(
        (entry) => entry.answer === "Fail"
      );
      if (hasChecklistFail && currentItem.inspection_result !== "Fail") {
        toast.error(
          "Inspection Result must be Fail because at least one checklist answer is Fail."
        );
        return;
      }
      if (!hasChecklistFail && currentItem.inspection_result === "Fail") {
        const shouldSubmitFailedInspection = window.confirm(
          "There are no checklist answers marked as Fail. Are you sure you want to submit a failed inspection?"
        );
        if (!shouldSubmitFailedInspection) {
          return;
        }
      }
    }

    const intentKey = String(currentItem.id);
    const inspectionRequest = inspectionOpenRequestRef.current;
    let activeIntent = null;
    setIsSaving(true);
    try {
      const token = sessionToken;
      const inspectorName = `${user?.name || ""} ${user?.surname || ""}`.trim();
      const inspectionInput = {
        id: currentItem.id,
        version: currentItem.version,
        inspector: inspectorName,
        draft: createInspectionDraftSnapshot(
          currentItem,
          inspectionChecklistAnswers,
          inspectImageFile
        ),
      };
      activeIntent = getOrCreateMutationIntent({
        registry: inspectionMutationIntentsRef.current,
        key: intentKey,
        input: inspectionInput,
        buildPayload: () => {
          const inspectedAt = new Date().toISOString();
          const updatedItem = {
            ...currentItem,
            inspection_date: inspectedAt,
            failure_notes: currentItem.failure_notes || null,
            image_uuid: currentItem.image_uuid || null,
            inspector: inspectorName,
            last_useredit: inspectorName,
            edit_date: inspectedAt,
          };

          if (checklistEnabled) {
            const normalizedChecklistAnswers = inspectionChecklistAnswers.map(
              (entry) => ({
                question: entry.question,
                answer: entry.answer,
              })
            );
            updatedItem.checklist_answers = normalizedChecklistAnswers;
            updatedItem.checklist_questions_snapshot =
              normalizedChecklistAnswers.map((entry) => entry.question);
            updatedItem.checklist_inspected_at = inspectedAt;
            updatedItem.checklist_inspector = inspectorName;
          }
          return updatedItem;
        },
      });
      const updatedItem = activeIntent.payload;

      if (inspectImageFile?.file && updatedItem.image_uuid) {
        setInspectUploadProgress(0);
        const formData = new FormData();
        formData.append("image_uuid", updatedItem.image_uuid);
        formData.append("file", inspectImageFile.file);

        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/upload-image`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: event => setInspectUploadProgress(event.total ? Math.min(100, Math.round(event.loaded * 100 / event.total)) : -1),
          }
        );
        setInspectUploadProgress(null);
      }

      if (inspectionRequest !== inspectionOpenRequestRef.current || token !== localStorage.getItem("token")) return;

      const outcome = await submitEquipmentUpdate({
        row: updatedItem,
        baseRow:
          originalRowsById.get(String(currentItem.id)) || currentItem,
        fields: [
          "inspection_date",
          "inspection_type",
          "inspection_result",
          "inspector",
          "failure_notes",
          "image_uuid",
          "checklist_answers",
          "checklist_questions_snapshot",
          "checklist_inspected_at",
          "checklist_inspector",
        ],
        token,
        mutationId: activeIntent.mutationId,
        actor: inspectorName,
        draftLabel: "inspection draft",
      });
      if (inspectionRequest !== inspectionOpenRequestRef.current || token !== localStorage.getItem("token")) return;
      if (outcome.saved) {
        inspectionMutationIntentsRef.current.delete(intentKey);
      } else if (outcome.mutationId) {
        const pendingItem = outcome.pendingItem || updatedItem;
        rememberMutationIntent({
          registry: inspectionMutationIntentsRef.current,
          key: intentKey,
          input: {
            id: pendingItem.id,
            version: pendingItem.version,
            inspector: inspectorName,
            draft: createInspectionDraftSnapshot(
              pendingItem,
              pendingItem.checklist_answers || inspectionChecklistAnswers,
              inspectImageFile
            ),
          },
          payload: pendingItem,
          mutationId: outcome.mutationId,
        });
      } else {
        inspectionMutationIntentsRef.current.delete(intentKey);
      }
      if (outcome.saved) {
        toast.success(
          outcome.restoredBeforeSave
            ? "Item restored and inspection updated successfully."
            : "Inspection updated successfully."
        );
        if (outcome.replayed) {
          const refreshed = await fetchEquipmentData(registerView, {
            preserveSelection: true,
            silent: true,
          });
          if (!refreshed) {
            toast.warning(
              "The retry was accepted, but the latest item could not be refreshed. Refresh the register before editing it again."
            );
          }
        } else {
          setEquipmentData((prev) =>
            prev.map((row) =>
              row.id === currentItem.id ? outcome.item : row
            )
          );
          setOriginalData((prev) =>
            prev.map((row) =>
              row.id === currentItem.id ? outcome.item : row
            )
          );
        }
        closeInspectDialog();
      } else {
        const pendingItem = outcome.pendingItem || updatedItem;
        const authoritativeBase = outcome.authoritativeBase || outcome.item;
        if (authoritativeBase) {
          setOriginalData((prev) =>
            prev.map((row) =>
              row.id === currentItem.id ? authoritativeBase : row
            )
          );
        }
        if (
          outcome.remoteState === "remote_deleted" ||
          outcome.remoteState === "remote_missing"
        ) {
          setEquipmentData((prev) =>
            prev.filter((row) => row.id !== currentItem.id)
          );
        } else if (outcome.item) {
          setEquipmentData((prev) =>
            prev.map((row) =>
              row.id === currentItem.id ? outcome.item : row
            )
          );
        }

        const alignedDraft = alignInspectionDraft({
          pendingItem,
          questions: checklistQuestions,
          replacementImage: inspectImageFile,
        });
        setCurrentItem(pendingItem);
        setInspectionChecklistAnswers(alignedDraft.checklistAnswers);
        if (!alignedDraft.keepReplacementImage && inspectImageFile) {
          revokeObjectUrl(inspectImagePreviewUrl);
          setInspectImageFile(null);
          setInspectImagePreviewUrl(null);
        }
        if (
          !alignedDraft.keepReplacementImage &&
          pendingItem.image_uuid !== currentItem.image_uuid
        ) {
          revokeObjectUrl(inspectExistingImageBlobUrl);
          setInspectExistingImageBlobUrl(null);
          void loadInspectPhoto(pendingItem.image_uuid);
        }
        const inspectionTagConflict = outcome.tagConflict;
        const inspectionConflictItem = inspectionTagConflict?.conflictingItem;
        toast.warning(
          inspectionTagConflict
            ? `${inspectionTagConflict.message} Conflicting active tag ${
                inspectionTagConflict.conflictingTag || "is unknown"
              }${inspectionConflictItem?.id ? ` (item ${inspectionConflictItem.id})` : ""}. ` +
              "Resolve that active item's tag, then select Save Inspection again; your draft is retained."
            : outcome.restoreDeclined
            ? "This item remains deleted. Select Save Inspection again to choose Restore and retry; your inspection draft is retained."
            : outcome.restorePending
            ? "The restore could not be confirmed after a temporary error. Select Save Inspection again to retry the exact restore request; your draft is retained."
            : outcome.remoteState === "remote_deleted"
            ? "This item remains deleted. Select Save Inspection again to restore and retry; your inspection draft is retained."
            : outcome.remoteState === "remote_missing"
            ? "This item no longer exists on the server. Your inspection draft is retained for review."
            : outcome.conflict
            ? "The inspection remains unsaved because this item changed elsewhere."
            : "Failed to update inspection data."
        );
      }
    } catch (err) {
      if (inspectionRequest !== inspectionOpenRequestRef.current) return;
      console.error("Error updating inspection data:", err);
      clearMutationIntentAfterError(
        inspectionMutationIntentsRef.current,
        intentKey,
        err
      );
      if (activeIntent?.payload) {
        const alignedDraft = alignInspectionDraft({
          pendingItem: activeIntent.payload,
          questions: checklistQuestions,
          replacementImage: inspectImageFile,
        });
        setCurrentItem(activeIntent.payload);
        setInspectionChecklistAnswers(alignedDraft.checklistAnswers);
      }
      toast.error("Error saving inspection data.");
    } finally {
      setInspectUploadProgress(null);
      setIsSaving(false);
    }
  };

  const handleViewLastInspection = async (id) => {
    if (!id) return;
    const request = ++lastInspectionRequestRef.current;
    const token = sessionToken;
    const isCurrent = () => request === lastInspectionRequestRef.current && token === localStorage.getItem("token");
    try {
      revokeObjectUrl(lastInspectionImageBlobUrl);
      setLastInspectionImageBlobUrl(null);
      setLastInspectionData(null);
      setLastInspectionDialogOpen(true);
      setLastInspectionLoading(true);
      setLastInspectionPhotoState("idle");

      const [inspectionResult, checklistResult] = await Promise.allSettled([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/inspection/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/equipment/${id}/checklist-latest`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (!isCurrent()) return;

      if (inspectionResult.status !== "fulfilled") {
        throw inspectionResult.reason;
      }

      const inspectionRow = inspectionResult.value?.data || {};
      const checklistPayload =
        checklistResult.status === "fulfilled" ? checklistResult.value?.data || null : null;

      if (checklistResult.status !== "fulfilled") {
        console.error("Error fetching checklist:", checklistResult.reason);
      }

      const combinedInspection = {
        equipmentId: inspectionRow.id || id,
        equipmentTag: inspectionRow.equipment_tag || checklistPayload?.equipmentTag || "Unknown",
        inspectionType: inspectionRow.inspection_type || null,
        inspectionResult: inspectionRow.inspection_result || null,
        inspectedAt: inspectionRow.inspection_date || checklistPayload?.inspectedAt || null,
        inspector: inspectionRow.inspector || checklistPayload?.inspector || null,
        notes: inspectionRow.failure_notes || null,
        imageUuid: inspectionRow.image_uuid || null,
        checklistAnswers: Array.isArray(checklistPayload?.answers) ? checklistPayload.answers : [],
        checklistLoadFailed: checklistResult.status !== "fulfilled",
      };

      setLastInspectionData(combinedInspection);

      if (combinedInspection.imageUuid) {
        setLastInspectionPhotoState("loading");
        void fetchImageBlobUrl(combinedInspection.imageUuid, { token }).then(imageBlobUrl => {
          if (!isCurrent()) { revokeObjectUrl(imageBlobUrl); return; }
          setLastInspectionImageBlobUrl(imageBlobUrl);
          setLastInspectionPhotoState("idle");
        }).catch(() => { if (isCurrent()) setLastInspectionPhotoState("unavailable"); });
      }
    } catch (error) {
      if (!isCurrent()) return;
      console.error("Error fetching last inspection:", error);
      toast.error("Failed to load last inspection.");
      setLastInspectionData(null);
    } finally {
      if (isCurrent()) setLastInspectionLoading(false);
    }
  };

  const handleCloseLastInspectionDialog = () => {
    lastInspectionRequestRef.current += 1;
    setLastInspectionDialogOpen(false);
    setLastInspectionLoading(false);
    setLastInspectionData(null);
    setZoomDialogOpen(false);
    setZoomedImageUrl(null);
    revokeObjectUrl(lastInspectionImageBlobUrl);
    setLastInspectionImageBlobUrl(null);
  };
  const handleZoomLastInspectionImage = () => {
    if (!lastInspectionImageBlobUrl) return;
    setZoomedImageUrl(lastInspectionImageBlobUrl);
    setZoomDialogOpen(true);
  };

  const handleZoomInspectImage = () => {
    const imageUrl = inspectImagePreviewUrl || inspectExistingImageBlobUrl;
    if (!imageUrl) return;
    setZoomedImageUrl(imageUrl);
    setZoomDialogOpen(true);
  };

  const handleCloseZoomDialog = () => {
    setZoomDialogOpen(false);
    setZoomedImageUrl(null);
  };
  const handleQrCodeClick = async (ids) => {
    if (!ids.length) return;
    try {
      const token = sessionToken;
      const responses = await Promise.all(
        ids.map((id) =>
          axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/equipment/${id}/qrcode`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          )
        )
      );
      const urls = responses.map((r) => r.data.qrCodeUrl);
      setQrCodeUrls(urls);
      setOpenQrDialog(true);
    } catch (err) {
      console.error("Error fetching QR codes:", err);
    }
  };
  const handlePrintQrCode = () => {
    if (!qrCodeUrls.length) return;
    const printWindow = window.open("", "_blank");
    const qrImages = qrCodeUrls
      .map(
        (url) => `
<div style="page-break-after: always; text-align: center; margin: 20px;">
<img src="${url}" alt="QR Code" style="max-width: 100%; max-height: 100%; margin-bottom: 20px;" />
</div>
`
      )
      .join("");
    printWindow.document.write(`
<html>
<head><title>Print QR Codes</title></head>
<body>${qrImages}</body>
</html>
`);
    printWindow.document.close();
    printWindow.print();
  };
  const getRowClassName = useCallback(
    (params) => {
      const classes = [];
      if (params.row.inspection_result === "Fail") {
        classes.push("fail-row");
      }
      if (Object.prototype.hasOwnProperty.call(editedRows, params.id)) {
        classes.push("dirty-row");
      }
      return classes.join(" ");
    },
    [editedRows]
  );

  const rowsForGrid = useMemo(() => {
    if (isDeletedView) {
      return equipmentData;
    }

    if (!dashboardFilter) return equipmentData;

    if (dashboardFilter.type === 'area') {
      const area = (dashboardFilter.area || '').toString();
      if (!area) return equipmentData;
      return equipmentData.filter((r) => (r.area || '').toString() === area);
    }

    if (dashboardFilter.type === 'inspectionDue') {
      const bucket = dashboardFilter.bucket;
      const months = Number.parseInt(dashboardFilter.months, 10) || DEFAULT_INSPECTION_VALIDITY_MONTHS;
      return equipmentData.filter((row) => getInspectionDueBucket(row.inspection_date, months) === bucket);
    }

    return equipmentData;
  }, [dashboardFilter, equipmentData, isDeletedView]);
  const renderLastInspectionDialogContent = () => {
    if (lastInspectionLoading) {
      return (
        <Box sx={{ py: 4, px: 3 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading last inspection details...
          </Typography>
        </Box>
      );
    }

    if (!lastInspectionData) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
          No last inspection data available.
        </Typography>
      );
    }

    const checklistAnswers = Array.isArray(lastInspectionData.checklistAnswers)
      ? lastInspectionData.checklistAnswers
      : [];
    const checklistCounts = checklistAnswers.reduce(
      (acc, entry) => {
        if (entry?.answer === "Pass") acc.pass += 1;
        else if (entry?.answer === "Fail") acc.fail += 1;
        else acc.na += 1;
        return acc;
      },
      { pass: 0, fail: 0, na: 0 }
    );
    const resultColor =
      lastInspectionData.inspectionResult === "Fail"
        ? "error"
        : lastInspectionData.inspectionResult === "Pass"
        ? "success"
        : "default";
    const metaCards = [
      { label: "Inspector", value: lastInspectionData.inspector || "N/A" },
      {
        label: "Timestamp",
        value: formatInspectionTimestamp(lastInspectionData.inspectedAt),
      },
      { label: "Type", value: lastInspectionData.inspectionType || "N/A" },
      { label: "Result", value: lastInspectionData.inspectionResult || "N/A" },
    ];

    return (
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          background:
            "linear-gradient(180deg, rgba(248,250,252,1) 0%, rgba(241,245,249,1) 100%)",
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 2.5 },
            mb: 2.5,
            borderRadius: 3,
            borderColor: isBranded ? alpha(brand.primary, 0.16) : "rgba(25, 118, 210, 0.16)",
            background: isBranded
              ? `linear-gradient(135deg, ${alpha(brand.primary, 0.06)} 0%, rgba(255,255,255,0.98) 65%)`
              : "linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(255,255,255,0.98) 65%)",
          }}
        >
          <Grid container spacing={2.5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{ letterSpacing: "0.12em", color: "text.secondary" }}
              >
                Last Inspection
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                {lastInspectionData.equipmentTag || "Unknown item"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Last recorded inspection summary for the selected item.
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
                <Chip
                  label={lastInspectionData.inspectionResult || "No result"}
                  color={resultColor}
                  variant={resultColor === "default" ? "outlined" : "filled"}
                  size="small"
                />
                <Chip
                  label={lastInspectionData.inspectionType || "No type"}
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={1.25}>
                {metaCards.map((card) => (
                  <Grid item xs={12} sm={6} key={card.label}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        backgroundColor: "rgba(255,255,255,0.92)",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {card.label}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 600 }}>
                        {card.value}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={5}>
            <Stack spacing={2.5}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  minHeight: 320,
                  borderRadius: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#fff",
                }}
              >
                {lastInspectionImageBlobUrl ? (
                  <img
                    src={lastInspectionImageBlobUrl}
                    alt="Last inspection"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "288px",
                      cursor: "zoom-in",
                      display: "block",
                    }}
                    onClick={handleZoomLastInspectionImage}
                    onError={() => {
                      setLastInspectionImageBlobUrl(null);
                      setLastInspectionPhotoState("unavailable");
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {lastInspectionPhotoState === "loading" ? "Loading attached photo…" : lastInspectionData?.imageUuid ? "Attached photo unavailable. Reopen this inspection to retry." : "No photo attached to this inspection."}
                  </Typography>
                )}
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 3,
                  minHeight: 180,
                  backgroundColor: "#fff",
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Notes
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-line", color: "text.primary" }}
                >
                  {lastInspectionData.notes || "No notes recorded for the last inspection."}
                </Typography>
              </Paper>
            </Stack>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2, md: 2.25 },
                borderRadius: 3,
                minHeight: "100%",
                backgroundColor: "#fff",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Checklist
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Answers captured during the last inspection.
                  </Typography>
                </Box>
                {checklistAnswers.length > 0 && (
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Chip label={`Pass ${checklistCounts.pass}`} color="success" size="small" />
                    <Chip label={`Fail ${checklistCounts.fail}`} color="error" size="small" />
                    <Chip label={`N/A ${checklistCounts.na}`} variant="outlined" size="small" />
                  </Box>
                )}
              </Box>

              {lastInspectionData.checklistLoadFailed ? (
                <Typography variant="body2" color="text.secondary">
                  Checklist details are unavailable right now.
                </Typography>
              ) : checklistAnswers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  This item does not have a recorded checklist yet.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: "52vh", overflowY: "auto", pr: 0.5 }}>
                  <Stack spacing={1}>
                    {checklistAnswers.map((entry, idx) => {
                      const isFail = entry?.answer === "Fail";
                      const answerLabel =
                        entry?.answer === "NA"
                          ? "N/A"
                          : entry?.answer || "N/A";

                      return (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            backgroundColor: isFail
                              ? "rgba(244, 67, 54, 0.08)"
                              : "rgba(248, 250, 252, 0.95)",
                            borderColor: isFail
                              ? "rgba(244, 67, 54, 0.50)"
                              : "rgba(15, 23, 42, 0.08)",
                          }}
                          key={`${entry.question}-${idx}`}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "space-between",
                              gap: 1.5,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {idx + 1}. {entry.question}
                            </Typography>
                            <Chip
                              label={answerLabel}
                              size="small"
                              color={isFail ? "error" : answerLabel === "Pass" ? "success" : "default"}
                              variant={answerLabel === "N/A" ? "outlined" : "filled"}
                            />
                          </Box>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };
  const renderInspectDialogContent = () => {
    if (!currentItem) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
          No inspection item selected.
        </Typography>
      );
    }

    const inspectorName = `${user?.name || ""} ${user?.surname || ""}`.trim() || "N/A";
    const selectedImageUrl = inspectImagePreviewUrl || inspectExistingImageBlobUrl;
    const checklistFailCount = inspectionChecklistAnswers.filter(
      (entry) => entry.answer === "Fail"
    ).length;
    const checklistPassCount = inspectionChecklistAnswers.filter(
      (entry) => entry.answer === "Pass"
    ).length;
    const checklistNaCount = inspectionChecklistAnswers.filter(
      (entry) => entry.answer === "NA"
    ).length;
    const allChecklistAnswersPassed =
      inspectionChecklistAnswers.length > 0 &&
      checklistPassCount === inspectionChecklistAnswers.length;
    const resultColor =
      currentItem.inspection_result === "Fail"
        ? "error"
        : currentItem.inspection_result === "Pass"
        ? "success"
        : "default";
    const detailChips = [
      currentItem.area ? `Area: ${currentItem.area}` : null,
      currentItem.zone ? `Zone: ${currentItem.zone}` : null,
      currentItem.system_component ? `System: ${currentItem.system_component}` : null,
    ].filter(Boolean);

    return (
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          background:
            "linear-gradient(180deg, rgba(248,250,252,1) 0%, rgba(241,245,249,1) 100%)",
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 2.5 },
            mb: 2.5,
            borderRadius: 3,
            borderColor: isBranded ? alpha(brand.primary, 0.16) : "rgba(25, 118, 210, 0.16)",
            background: isBranded
              ? `linear-gradient(135deg, ${alpha(brand.primary, 0.06)} 0%, rgba(255,255,255,0.98) 65%)`
              : "linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(255,255,255,0.98) 65%)",
          }}
        >
          <Grid container spacing={2.5} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="overline"
                sx={{ letterSpacing: "0.12em", color: "text.secondary" }}
              >
                Inspection Session
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                {currentItem.equipment_tag || "Untitled item"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Update the inspection result, complete the checklist, and attach the current note
                and photo evidence in one place.
              </Typography>
              {detailChips.length > 0 && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
                  {detailChips.map((label) => (
                    <Chip key={label} label={label} variant="outlined" size="small" />
                  ))}
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={5}>
              <Grid container spacing={1.25}>
                <Grid item xs={12} sm={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      backgroundColor: "rgba(255,255,255,0.92)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Inspector
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 600 }}>
                      {inspectorName}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      backgroundColor: "rgba(255,255,255,0.92)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Last Recorded
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 600 }}>
                      {formatInspectionTimestamp(currentItem.inspection_date)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      backgroundColor: "rgba(255,255,255,0.92)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Inspection Type
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 600 }}>
                      {currentItem.inspection_type || "Not set"}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      backgroundColor: "rgba(255,255,255,0.92)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Result
                    </Typography>
                    <Box sx={{ mt: 0.75 }}>
                      <Chip
                        label={currentItem.inspection_result || "Pending"}
                        color={resultColor}
                        variant={resultColor === "default" ? "outlined" : "filled"}
                        size="small"
                      />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={5}>
            <Stack spacing={2.5}>
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2, md: 2.25 },
                  borderRadius: 3,
                  backgroundColor: "#fff",
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Inspection Setup
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  These values will be saved when you submit the inspection.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      select
                      name="inspection_type"
                      label="Inspection Type"
                      value={currentItem.inspection_type || ""}
                      onChange={handleInspectChange}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="" disabled>
                        Select Type
                      </MenuItem>
                      <MenuItem value="Visual">Visual</MenuItem>
                      <MenuItem value="Close">Close</MenuItem>
                      <MenuItem value="Detailed">Detailed</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      select
                      name="inspection_result"
                      label="Inspection Result"
                      value={currentItem.inspection_result || ""}
                      onChange={handleInspectChange}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="" disabled>
                        Select Result
                      </MenuItem>
                      <MenuItem value="Pass">Pass</MenuItem>
                      <MenuItem value="Fail">Fail</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Inspector Name"
                      value={inspectorName}
                      fullWidth
                      size="small"
                      disabled
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2, md: 2.25 },
                  borderRadius: 3,
                  backgroundColor: "#fff",
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Item Photo
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Attach one current photo for this item. Clicking the preview opens zoom.
                </Typography>
                <Stack spacing={2}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      minHeight: 220,
                      borderRadius: 2.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(248,250,252,0.85)",
                    }}
                  >
                    {selectedImageUrl ? (
                      <img
                        key={selectedImageUrl}
                        src={selectedImageUrl}
                        alt="Inspection preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "188px",
                          display: "block",
                          cursor: "zoom-in",
                        }}
                        onClick={handleZoomInspectImage}
                        onError={() => {
                          if (inspectImageFile) {
                            setInspectPhotoError("This photo preview could not be displayed. Attach the photo again to check it before saving.");
                          } else {
                            setInspectExistingImageBlobUrl(null);
                            setInspectPhotoState("unavailable");
                            setInspectPhotoError("The attached photo could not be displayed. Retry or attach a replacement.");
                          }
                        }}
                      />
                    ) : inspectPhotoState === "loading" ? (
                      <Stack alignItems="center" spacing={1} role="status">
                        <CircularProgress size={28} />
                        <Typography variant="body2">Loading attached photo…</Typography>
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {currentItem?.image_uuid ? "Attached photo unavailable." : "No photo attached yet."}
                      </Typography>
                    )}
                  </Paper>
                  {inspectPhotoState === "preparing" && (
                    <Box role="status"><LinearProgress /><Typography variant="body2" sx={{ mt: 1 }}>Preparing photo… Your previous attachment is kept until the new photo is ready.</Typography></Box>
                  )}
                  {inspectPhotoError && (
                    <Typography role="alert" variant="body2" color="error">{inspectPhotoError}</Typography>
                  )}
                  {inspectImageFile && inspectPhotoState !== "preparing" && !isSaving && (
                    <Typography variant="body2" color="text.secondary" role="status">
                      Ready to upload · {inspectImageFile.width} × {inspectImageFile.height} · {(inspectImageFile.file.size / 1024).toFixed(0)} KB
                    </Typography>
                  )}
                  {inspectUploadProgress !== null && (
                    <Box role="status"><LinearProgress variant={inspectUploadProgress < 0 ? "indeterminate" : "determinate"} value={Math.max(0, inspectUploadProgress)} /><Typography variant="body2" sx={{ mt: 1 }}>Uploading photo… {inspectUploadProgress >= 0 ? `${inspectUploadProgress}%` : ""}</Typography></Box>
                  )}
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button variant="outlined" onClick={handleAttachImageClick} disabled={isSaving}>
                      Attach / Replace Photo
                    </Button>
                    {inspectPhotoState === "unavailable" && <Button variant="outlined" onClick={() => loadInspectPhoto(currentItem?.image_uuid)} disabled={isSaving}>Retry photo</Button>}
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleRemoveInspectImage}
                      disabled={
                        isSaving || (!currentItem?.image_uuid &&
                        !inspectImagePreviewUrl &&
                        !inspectExistingImageBlobUrl && inspectPhotoState !== "preparing")
                      }
                    >
                      Remove Photo
                    </Button>
                  </Box>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleInspectImageChange}
                  />
                </Stack>
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2, md: 2.25 },
                  borderRadius: 3,
                  backgroundColor: "#fff",
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Item Notes
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Capture the note that should remain attached to the item after this inspection.
                </Typography>
                <TextField
                  placeholder="Enter item notes"
                  name="failure_notes"
                  value={currentItem.failure_notes || ""}
                  onChange={handleInspectChange}
                  fullWidth
                  multiline
                  minRows={6}
                />
              </Paper>
            </Stack>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2, md: 2.25 },
                borderRadius: 3,
                backgroundColor: "#fff",
                minHeight: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Inspection Checklist
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Complete each answer before submitting the inspection.
                  </Typography>
                </Box>
                {checklistConfig.checklistEnabled && inspectionChecklistAnswers.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      type="button"
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<CheckCircleOutlineIcon />}
                      onClick={handlePassAllChecklist}
                      disabled={
                        checklistConfigLoading || isSaving || allChecklistAnswersPassed
                      }
                      aria-label={`Mark all ${inspectionChecklistAnswers.length} checklist ${
                        inspectionChecklistAnswers.length === 1 ? "question" : "questions"
                      } as Pass`}
                      sx={{
                        minHeight: 30,
                        px: 1.25,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 750,
                        borderColor: "rgba(47,125,74,0.42)",
                        backgroundColor: "rgba(47,125,74,0.04)",
                        "&:hover": {
                          borderColor: "success.main",
                          backgroundColor: "rgba(47,125,74,0.09)",
                        },
                      }}
                    >
                      {allChecklistAnswersPassed ? "All passed" : "Pass all"}
                    </Button>
                    <Chip label={`Pass ${checklistPassCount}`} color="success" size="small" />
                    <Chip label={`Fail ${checklistFailCount}`} color="error" size="small" />
                    <Chip label={`N/A ${checklistNaCount}`} variant="outlined" size="small" />
                  </Box>
                )}
              </Box>

              {checklistConfigLoading ? (
                <Box sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Loading inspection checklist…</Typography>
                  <LinearProgress aria-label="Loading inspection checklist" />
                </Box>
              ) : checklistConfigError ? (
                <Box role="alert">
                  <Typography variant="body2" color="error">{checklistConfigError}</Typography>
                  <Button onClick={() => {
                    if (inspectionSourceItemRef.current) void loadInspectionChecklist(inspectionSourceItemRef.current, inspectionOpenRequestRef.current);
                  }}>Retry checklist</Button>
                </Box>
              ) : !checklistConfig.checklistEnabled ? (
                <Typography variant="body2" color="text.secondary">
                  Checklist is disabled for this database.
                </Typography>
              ) : inspectionChecklistAnswers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Checklist enabled, but no checklist questions are configured.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: "64vh", overflowY: "auto", pr: 0.5 }}>
                  <Stack spacing={1}>
                    {inspectionChecklistAnswers.map((entry, idx) => {
                      const currentAnswer = entry.answer || "";
                      const isFail = currentAnswer === "Fail";
                      return (
                        <Paper
                          key={`${entry.question}-${idx}`}
                          variant="outlined"
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            backgroundColor: isFail
                              ? "rgba(244, 67, 54, 0.08)"
                              : "rgba(248, 250, 252, 0.95)",
                            borderColor: isFail
                              ? "rgba(244, 67, 54, 0.50)"
                              : "rgba(15, 23, 42, 0.08)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "space-between",
                              gap: 1.5,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {idx + 1}. {entry.question}
                            </Typography>
                            {currentAnswer && (
                              <Chip
                                label={currentAnswer === "NA" ? "N/A" : currentAnswer}
                                size="small"
                                color={
                                  currentAnswer === "Fail"
                                    ? "error"
                                    : currentAnswer === "Pass"
                                    ? "success"
                                    : "default"
                                }
                                variant={currentAnswer === "NA" ? "outlined" : "filled"}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                            {CHECKLIST_ANSWER_OPTIONS.map((option) => (
                              <FormControlLabel
                                key={`${entry.question}-${option}`}
                                control={
                                  <Checkbox
                                    checked={entry.answer === option}
                                    disabled={isSaving}
                                    onChange={() =>
                                      handleChecklistAnswerChange(entry.question, option)
                                    }
                                  />
                                }
                                label={option === "NA" ? "N/A" : option}
                                sx={{ mr: 1.5 }}
                              />
                            ))}
                          </Box>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };
  const wideFieldSet = new Set(["description", "system_component"]);
  const selectedRowCount = selectedRows.length;
  const hasActiveGridDraft = isEditingAnyRow;
  const mutationActionsBlocked =
    batchActionInProgress || hasUnsavedChanges || hasActiveGridDraft;
  const selectionLabel = selectedRowCount
    ? `${selectedRowCount} ${selectedRowCount === 1 ? "item" : "items"} selected`
    : "No items selected";
  const passAllFailAnswerCount = inspectionChecklistAnswers.filter(
    (entry) => entry.answer === "Fail"
  ).length;
  const passAllNaAnswerCount = inspectionChecklistAnswers.filter(
    (entry) => entry.answer === "NA"
  ).length;
  const passAllReplacementLabel = [
    passAllFailAnswerCount > 0
      ? `${passAllFailAnswerCount} Fail ${
          passAllFailAnswerCount === 1 ? "answer" : "answers"
        }`
      : null,
    passAllNaAnswerCount > 0
      ? `${passAllNaAnswerCount} N/A ${
          passAllNaAnswerCount === 1 ? "answer" : "answers"
        }`
      : null,
  ]
    .filter(Boolean)
    .join(" and ");
  const unsavedRowsLabel = `${editedRowCount} unsaved ${
    editedRowCount === 1 ? "row" : "rows"
  }`;
  const saveStatusLabel = isBatchSaving
    ? `Saving ${editedRowCount} ${editedRowCount === 1 ? "row" : "rows"}…`
    : isDiscardingChanges
    ? "Discarding changes…"
    : hasActiveGridDraft
    ? "Finish editing the current row"
    : hasUnsavedChanges
    ? unsavedRowsLabel
    : "All changes saved";
  const saveStatusHint = isBatchSaving
    ? "Sending changes to the server."
    : isDiscardingChanges
    ? "Restoring the last saved values."
    : hasActiveGridDraft
    ? "Press Enter to finish, or Escape to cancel."
    : hasUnsavedChanges
    ? "Changes are stored locally until saved."
    : "The register matches the server.";

  return (
    <>
      <Paper
        elevation={0}
        square
        sx={{
          position: "absolute",
          top: "60px",
          bottom: "64px",
          left: 0,
          right: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundImage: "none",
        }}
      >
        {isBranded && (
          <Box className="branded-register-heading">
            <Typography component="h1" className="branded-register-eyebrow">{brand.name} equipment register</Typography>
          </Box>
        )}
        <Box sx={{ flex: 1, minHeight: 0, p: 2, display: "flex" }}>
          <DataGridPro
            showCellVerticalBorder
            showColumnVerticalBorder
            filterModel={filterModel}
            onFilterModelChange={(model) =>
              setFilterModel({
                ...model,
                quickFilterExcludeHiddenColumns: false,
              })
            }
            density={density}
            loading={loading}
            rows={rowsForGrid}
            columns={gridColumns}
            columnVisibilityModel={columnVisibilityModel}
            onColumnResize={(params) => {
              clearTimeout(resizeTimeout);
              resizeTimeout = setTimeout(() => {
                setColumnWidths((prevWidths) => {
                  const newWidths = {
                    ...prevWidths,
                    [params.colDef.field]: params.width,
                  };
                  localStorage.setItem(
                    "columnWidths",
                    JSON.stringify(newWidths)
                  );
                  return newWidths;
                });
              }, 150);
            }}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            getRowClassName={getRowClassName}
            editMode="row"
            isCellEditable={(params) =>
              Boolean(params.colDef.editable) && !batchActionInProgress
            }
            rowModesModel={rowModesModel}
            onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={onProcessRowUpdateError}
            pagination
            pageSizeOptions={[5, 10, 20, 50, 100]}
            slots={{ toolbar: CustomToolbar }}
            slotProps={{ toolbar: { hiddenEditableFieldCount, isEditingAnyRow } }}
            onDensityChange={(newDensity) => setDensity(newDensity)}
            rowSelectionMode="multiple"
            rowMultiSelectionWithClick={false}
            disableRowSelectionOnClick={false}
            onRowSelectionModelChange={(ids) => setSelectedRows(ids)}
            sx={{
              flex: 1,
              "--DataGrid-rowBorderColor": "#e7ecf1",
              /* Typography tuning (clarity-first) */
              fontFamily:
                "'IBM Plex Sans', 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              fontSize: 15,
              "& .MuiDataGrid-columnHeaders": {
                fontSize: 15,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
              },
              "& .MuiDataGrid-cell": {
                fontWeight: 400,
              },
              "& .MuiDataGrid-row.dirty-row:not(.Mui-selected) .MuiDataGrid-cell":
                {
                  backgroundColor: "rgba(245, 158, 11, 0.08)",
                },
              "& .MuiDataGrid-row.dirty-row:not(.fail-row) .MuiDataGrid-cell:first-of-type":
                {
                  boxShadow: "inset 3px 0 0 #f59e0b",
                },
              "& .MuiDataGrid-cell.pending-edit-cell": {
                backgroundColor: "rgba(245, 158, 11, 0.18)",
                fontWeight: 600,
              },
              // Subtle background only for non-selected failed rows
              "& .MuiDataGrid-row.fail-row:not(.Mui-selected) .MuiDataGrid-cell":
                {
                  backgroundColor: "rgba(244, 67, 54, 0.08)", // light red tint, low alpha
                },
              // Add a left accent bar on failed rows for quick scanning
              "& .MuiDataGrid-row.fail-row .MuiDataGrid-cell:first-of-type": {
                boxShadow: "inset 3px 0 0 #f44336", // material error red
              },
              // Preserve strong selection highlight even if row is failed
              "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell": {
                backgroundColor: "var(--register-selection, rgba(25, 118, 210, 0.18)) !important",
              },
              "& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell": {
                backgroundColor: "var(--register-selection-hover, rgba(25, 118, 210, 0.24)) !important",
              },
              // Keep inspector column styling
              ".inspector-column": {
                backgroundColor: "#f5f5f5 !important",
                color: "#808080",
              },
            }}
          />
        </Box>
      </Paper>
      {/* BOTTOM ACTION TOOLBAR */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          minHeight: 64,
          background: isBranded ? brand.navBackground : "linear-gradient(135deg, #0b0d12, #111622)",
          borderTop: isBranded ? `2px solid ${brand.accent}` : "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 -10px 28px rgba(0,0,0,0.30)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Box
          role="group"
          aria-label="Equipment register actions"
          sx={{
            minHeight: 64,
            px: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              overflowX: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(255,255,255,0.22) transparent",
              pb: 0.25,
              "&::-webkit-scrollbar": { height: 4 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(255,255,255,0.22)",
                borderRadius: 999,
              },
            }}
          >
          {!isDeletedView && !isReadOnlyUser && (
            <BottomActionButton
              tone="primary"
              tooltip={
                mutationActionsBlocked
                  ? "Save or discard the current changes before adding an item."
                  : "Add a new equipment item"
              }
              aria-label="Add item"
              startIcon={<AddCircleIcon />}
              onClick={() => {
                addMutationIntentsRef.current.delete("add");
                setOpenAddDialog(true);
              }}
              disabled={mutationActionsBlocked}
            >
              Add item
            </BottomActionButton>
          )}
          {!isDeletedView && !isReadOnlyUser && (
            <BottomActionButton
              tone="danger"
              tooltip={
                mutationActionsBlocked
                  ? "Save or discard the current changes before deleting items."
                  : selectedRowCount
                  ? `Move ${selectedRowCount} selected ${
                      selectedRowCount === 1 ? "item" : "items"
                    } to Deleted Items`
                  : "Select at least one item to delete"
              }
              aria-label={`Delete ${selectedRowCount} selected ${
                selectedRowCount === 1 ? "item" : "items"
              }`}
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
              disabled={selectedRowCount === 0 || mutationActionsBlocked}
              sx={{ color: selectedRowCount ? "#ffd0d0" : undefined }}
            >
              {selectedRowCount ? `Delete ${selectedRowCount}` : "Delete"}
            </BottomActionButton>
          )}
          {!isDeletedView && (user?.role === "admin" || user?.role === "inspector") && (
            <BottomActionButton
              tone="primary"
              tooltip={
                mutationActionsBlocked
                  ? "Save or discard the current changes before starting an inspection."
                  : selectedRowCount === 1
                  ? "Inspect the selected item"
                  : "Select exactly one item to inspect"
              }
              aria-label="Inspect selected item"
              startIcon={<Visibility />}
              onClick={() => handleInspectClick(selectedRows[0])}
              disabled={selectedRowCount !== 1 || mutationActionsBlocked}
            >
              Inspect
            </BottomActionButton>
          )}
          {!isDeletedView && (
            <BottomActionButton
              tooltip={
                selectedRowCount
                  ? `Generate QR ${selectedRowCount === 1 ? "code" : "codes"} for the selection`
                  : "Select at least one item to generate QR codes"
              }
              aria-label={`Generate QR codes for ${selectedRowCount} selected ${
                selectedRowCount === 1 ? "item" : "items"
              }`}
              startIcon={<QrCode />}
              onClick={() =>
                selectedRowCount && handleQrCodeClick(selectedRows)
              }
              disabled={selectedRowCount === 0 || batchActionInProgress}
            >
              {selectedRowCount > 1 ? `QR codes ${selectedRowCount}` : "QR code"}
            </BottomActionButton>
          )}
          <BottomActionButton
            tooltip={
              selectedRowCount === 1
                ? "View the selected item's last inspection"
                : "Select exactly one item to view its last inspection"
            }
            aria-label="View last inspection for selected item"
            startIcon={<NoteIcon />}
            onClick={() => handleViewLastInspection(selectedRows[0])}
            disabled={selectedRowCount !== 1 || batchActionInProgress}
          >
            Last inspection
          </BottomActionButton>
          <BottomActionButton
            tooltip={
              selectedRowCount === 1
                ? "View change and inspection history"
                : "Select exactly one item to view its history"
            }
            aria-label="View history for selected item"
            startIcon={<HistoryIcon />}
            onClick={handleOpenHistory}
            disabled={selectedRowCount !== 1 || batchActionInProgress}
          >
            History
          </BottomActionButton>
          {isDeletedView && !isReadOnlyUser && (
            <BottomActionButton
              tone="success"
              tooltip={
                selectedRowCount
                  ? `Restore ${selectedRowCount} selected ${
                      selectedRowCount === 1 ? "item" : "items"
                    }`
                  : "Select at least one deleted item to restore"
              }
              aria-label={`Restore ${selectedRowCount} selected ${
                selectedRowCount === 1 ? "item" : "items"
              }`}
              startIcon={<RestoreFromTrashIcon />}
              onClick={() => {
                if (!selectedRowCount) {
                  toast.warn("Please select at least one row to restore.");
                  return;
                }
                setShowRestoreConfirm(true);
              }}
              disabled={selectedRowCount === 0 || batchActionInProgress}
              sx={{ color: selectedRowCount ? "#c9f7d8" : undefined }}
            >
              {selectedRowCount ? `Restore ${selectedRowCount}` : "Restore"}
            </BottomActionButton>
          )}

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 0.5, borderColor: "rgba(255,255,255,0.13)" }}
          />
          <Chip
            label={selectionLabel}
            size="small"
            variant="outlined"
            sx={{
              flexShrink: 0,
              minWidth: 124,
              height: 30,
              color: selectedRowCount ? "#ffffff" : "rgba(255,255,255,0.58)",
              borderColor: selectedRowCount
                ? (isBranded ? "rgba(255,255,255,0.42)" : "rgba(93, 173, 226, 0.58)")
                : "rgba(255,255,255,0.13)",
              backgroundColor: selectedRowCount
                ? (isBranded ? "rgba(255,255,255,0.12)" : "rgba(31,111,178,0.22)")
                : "rgba(255,255,255,0.03)",
              "& .MuiChip-label": { px: 1.2, fontWeight: 700, fontSize: 12 },
            }}
          />
          </Box>

          <Box
            sx={{
              pl: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              borderLeft: "1px solid rgba(255,255,255,0.13)",
              flexShrink: 0,
              backgroundColor: isBranded ? brand.navBackground : "rgba(11,13,18,0.90)",
              boxShadow: "-12px 0 22px rgba(4,7,12,0.18)",
            }}
          >
            <Stack
              ref={registerStatusRef}
              spacing={0.05}
              role="status"
              aria-live="polite"
              tabIndex={-1}
              sx={{ minWidth: 178, maxWidth: 210, outline: "none" }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center">
                {!hasUnsavedChanges && !hasActiveGridDraft && !batchActionInProgress ? (
                  <CheckCircleOutlineIcon sx={{ color: "#5fce8b", fontSize: 17 }} />
                ) : (
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      flexShrink: 0,
                      backgroundColor: hasActiveGridDraft ? "#73b9eb" : "#f4dc21",
                      boxShadow: hasActiveGridDraft
                        ? "0 0 0 3px rgba(115,185,235,0.13)"
                        : "0 0 0 3px rgba(244,220,33,0.12)",
                    }}
                  />
                )}
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.92)", fontWeight: 800, lineHeight: 1.2 }}
                >
                  {saveStatusLabel}
                </Typography>
              </Stack>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.52)", fontSize: 10.5, lineHeight: 1.2 }}
              >
                {saveStatusHint}
              </Typography>
            </Stack>

            <BottomActionButton
              tone="danger"
              tooltip={
                hasActiveGridDraft
                  ? "Finish or cancel the current row edit first."
                  : hasUnsavedChanges
                  ? `Discard ${unsavedRowsLabel}`
                  : "There are no unsaved changes to discard"
              }
              aria-label={`Discard ${editedRowCount} unsaved ${
                editedRowCount === 1 ? "row" : "rows"
              }`}
              startIcon={<CancelIcon />}
              onClick={() => setDiscardChangesDialogOpen(true)}
              disabled={
                !hasUnsavedChanges || hasActiveGridDraft || batchActionInProgress
              }
              sx={{
                color: "#ffd0d0",
              }}
            >
              Discard
            </BottomActionButton>

            <Tooltip
              title={
                hasActiveGridDraft
                  ? "Finish the current row edit before saving."
                  : hasUnsavedChanges
                  ? "Save changes to the server (Ctrl/⌘+S)"
                  : "There are no unsaved changes"
              }
              placement="top"
              arrow
            >
              <span style={{ display: "inline-flex" }}>
                <Button
                  size="small"
                  variant="contained"
                  aria-label={`Save ${editedRowCount} unsaved ${
                    editedRowCount === 1 ? "row" : "rows"
                  }`}
                  aria-keyshortcuts="Control+S Meta+S"
                  startIcon={
                    isBatchSaving ? (
                      <CircularProgress size={16} thickness={5} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={handleBatchSave}
                  disabled={
                    !hasUnsavedChanges || hasActiveGridDraft || batchActionInProgress
                  }
                  sx={{
                    minHeight: 40,
                    minWidth: 132,
                    px: 1.75,
                    borderRadius: 2,
                    color: isBranded ? "#ffffff" : "#11151b",
                    backgroundColor: isBranded ? "primary.main" : "#f4dc21",
                    border: isBranded ? `1px solid ${brand.accent}` : "1px solid rgba(255,240,104,0.76)",
                    fontSize: 12.5,
                    fontWeight: 850,
                    whiteSpace: "nowrap",
                    boxShadow: isBranded ? `0 5px 14px ${alpha(brand.primary, 0.18)}` : "0 5px 14px rgba(244,220,33,0.18)",
                    "&:hover": {
                      backgroundColor: isBranded ? "primary.dark" : "#ffe64d",
                      boxShadow: isBranded ? `0 7px 18px ${alpha(brand.primary, 0.26)}` : "0 7px 18px rgba(244,220,33,0.26)",
                    },
                    "&.Mui-disabled": {
                      color: "rgba(255,255,255,0.34)",
                      backgroundColor: isBranded ? "rgba(255,255,255,0.06)" : "rgba(244,220,33,0.11)",
                      borderColor: isBranded ? "rgba(255,255,255,0.12)" : "rgba(244,220,33,0.16)",
                      boxShadow: "none",
                    },
                    "&:focus-visible": {
                      outline: "2px solid #ffffff",
                      outlineOffset: 2,
                    },
                  }}
                >
                  {isBatchSaving
                    ? "Saving…"
                    : hasUnsavedChanges
                    ? `Save ${editedRowCount} ${editedRowCount === 1 ? "change" : "changes"}`
                    : "Save changes"}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      <Dialog
        open={discardChangesDialogOpen}
        onClose={() => {
          if (!isDiscardingChanges) {
            setDiscardChangesDialogOpen(false);
          }
        }}
        aria-labelledby="discard-changes-title"
        aria-describedby="discard-changes-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="discard-changes-title">Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography id="discard-changes-description" color="text.secondary">
              {editedRowCount} {editedRowCount === 1 ? "row has" : "rows have"} changes
              that have not been saved to the server.
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderColor: "rgba(211, 47, 47, 0.24)",
                backgroundColor: "rgba(211, 47, 47, 0.045)",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 650 }}>
                Discarding restores the last saved values and cannot be undone.
              </Typography>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            autoFocus
            onClick={() => setDiscardChangesDialogOpen(false)}
            disabled={isDiscardingChanges}
          >
            Keep editing
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={
              isDiscardingChanges ? (
                <CircularProgress size={16} thickness={5} color="inherit" />
              ) : (
                <CancelIcon />
              )
            }
            onClick={handleBatchCancel}
            disabled={isDiscardingChanges}
          >
            {isDiscardingChanges
              ? "Discarding…"
              : `Discard ${editedRowCount} ${editedRowCount === 1 ? "row" : "rows"}`}
          </Button>
        </DialogActions>
      </Dialog>
      {/* ADD NEW ITEM DIALOG */}
      <Dialog
        open={openAddDialog}
        onClose={() => {
          addMutationIntentsRef.current.delete("add");
          setOpenAddDialog(false);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack spacing={0.5}>
            <Typography variant="h5">Add New Equipment Item</Typography>
            <Typography variant="body2" color="text.secondary">
              Copy an existing item to speed up entry, then update the area and
              tag for the new record.
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                backgroundColor: "rgba(248, 249, 252, 0.9)",
              }}
            >
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Copy Item
                  </Typography>
                  <Divider flexItem sx={{ flex: 1 }} />
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Search by equipment tag"
                      value={copySearchTag}
                      onChange={(e) => setCopySearchTag(e.target.value)}
                      placeholder="Type tag to filter"
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Search by model number"
                      value={copySearchModel}
                      onChange={(e) => setCopySearchModel(e.target.value)}
                      placeholder="Type model no. to filter"
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      options={filteredCopyItems}
                      value={selectedCopyItem}
                      onChange={(event, newValue) => {
                        setSelectedCopyItem(newValue);
                        handleCopyItemSelection(newValue);
                      }}
                      getOptionLabel={(option) => {
                        const tag = option?.equipment_tag || "No tag";
                        const model = option?.model_no || "No model";
                        const description = option?.description
                          ? ` • ${option.description}`
                          : "";
                        return `${tag} • ${model}${description}`;
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      noOptionsText={
                        copySearchTag || copySearchModel
                          ? "No matching items found"
                          : "Start typing to find an item to copy"
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select item to copy"
                          placeholder="Choose a matching item to populate details"
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    New Item Details
                  </Typography>
                  <Divider flexItem sx={{ flex: 1 }} />
                </Stack>
                <Grid container spacing={2}>
                  {allColumns
                    .filter((col) => col.editable)
                    .map((col) => {
                      const isWideField = wideFieldSet.has(col.field);
                      if (col.field === "zone") {
                        return (
                          <Grid
                            item
                            xs={12}
                            md={isWideField ? 12 : 6}
                            key={col.field}
                          >
                            <TextField
                              select
                              label={col.headerName}
                              name="zone"
                              value={newItemValues.zone || ""}
                              onChange={(e) =>
                                setNewItemValues((prev) => ({
                                  ...prev,
                                  zone: e.target.value,
                                }))
                              }
                              fullWidth
                              size="small"
                            >
                              <MenuItem value="" disabled>
                                Select Zone
                              </MenuItem>
                              {zoneValueOptions.map((zoneOption) => (
                                <MenuItem key={zoneOption} value={zoneOption}>
                                  {zoneOption}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        );
                      }
                      if (col.field === "area") {
                        return (
                          <Grid
                            item
                            xs={12}
                            md={isWideField ? 12 : 6}
                            key={col.field}
                          >
                            <Autocomplete
                              freeSolo
                              options={areaOptions}
                              inputValue={newItemValues.area || ""}
                              onInputChange={(event, newInputValue) =>
                                setNewItemValues((prev) => ({
                                  ...prev,
                                  area: newInputValue,
                                }))
                              }
                              onChange={(event, newValue) =>
                                setNewItemValues((prev) => ({
                                  ...prev,
                                  area: newValue || "",
                                }))
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={col.headerName}
                                  placeholder="Start typing to filter existing areas"
                                  fullWidth
                                  size="small"
                                />
                              )}
                            />
                          </Grid>
                        );
                      }
                      if (col.field === "equipment_tag") {
                        return (
                          <Grid
                            item
                            xs={12}
                            md={isWideField ? 12 : 6}
                            key={col.field}
                          >
                            <Autocomplete
                              freeSolo
                              options={tagOptions}
                              inputValue={newItemValues.equipment_tag || ""}
                              onInputChange={(event, newInputValue) =>
                                setNewItemValues((prev) => ({
                                  ...prev,
                                  equipment_tag: newInputValue,
                                }))
                              }
                              onChange={(event, newValue) =>
                                setNewItemValues((prev) => ({
                                  ...prev,
                                  equipment_tag: newValue || "",
                                }))
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={col.headerName}
                                  placeholder="Start typing to filter existing tags"
                                  fullWidth
                                  size="small"
                                />
                              )}
                            />
                          </Grid>
                        );
                      }
                      return (
                        <Grid
                          item
                          xs={12}
                          md={isWideField ? 12 : 6}
                          key={col.field}
                        >
                          <TextField
                            label={col.headerName}
                            name={col.field}
                            value={newItemValues[col.field] || ""}
                            onChange={handleNewItemChange}
                            placeholder={`Enter ${col.headerName.toLowerCase()}`}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                      );
                    })}
                </Grid>
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              addMutationIntentsRef.current.delete("add");
              setOpenAddDialog(false);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleAddItem}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* INSPECT DIALOG */}
      <Dialog
        open={openInspectDialog}
        onClose={handleCloseInspectDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            width: "min(1180px, 96vw)",
            maxWidth: "96vw",
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>Inspect Equipment</DialogTitle>
        <DialogContent dividers sx={{ p: 0, maxHeight: "82vh" }}>
          {renderInspectDialogContent()}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          {isSaving && <LinearProgress sx={{ width: "100%" }} />}
          <Button onClick={handleCloseInspectDialog} color="primary" disabled={isSaving}>
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitInspect}
            disabled={isSaving || inspectPhotoState === "preparing" || checklistConfigLoading || !!checklistConfigError}
          >
            {isSaving ? "Submitting…" : inspectPhotoState === "preparing" ? "Preparing photo…" : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={discardInspectionDialogOpen}
        onClose={() => setDiscardInspectionDialogOpen(false)}
        aria-labelledby="discard-inspection-title"
        aria-describedby="discard-inspection-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="discard-inspection-title">
          Discard inspection draft?
        </DialogTitle>
        <DialogContent dividers>
          <Typography id="discard-inspection-description" variant="body2">
            Your inspection changes have not been submitted. Closing now will discard
            the checklist answers, inspection details, notes, and photo changes from
            this session.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button autoFocus onClick={() => setDiscardInspectionDialogOpen(false)}>
            Continue inspection
          </Button>
          <Button variant="contained" color="error" onClick={closeInspectDialog}>
            Discard draft
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={passAllChecklistDialogOpen}
        onClose={() => setPassAllChecklistDialogOpen(false)}
        aria-labelledby="pass-all-checklist-title"
        aria-describedby="pass-all-checklist-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="pass-all-checklist-title">
          Mark every question as Pass?
        </DialogTitle>
        <DialogContent dividers>
          <Typography id="pass-all-checklist-description" variant="body2">
            This will replace {passAllReplacementLabel}. Changes are not saved until you
            submit the inspection.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button autoFocus onClick={() => setPassAllChecklistDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={applyPassAllChecklist}
          >
            Pass all
          </Button>
        </DialogActions>
      </Dialog>

      {/* LAST INSPECTION DIALOG */}
      <Dialog
        open={lastInspectionDialogOpen}
        onClose={handleCloseLastInspectionDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            width: "min(1180px, 96vw)",
            maxWidth: "96vw",
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>Last Inspection</DialogTitle>
        <DialogContent dividers sx={{ p: 0, maxHeight: "82vh" }}>
          {renderLastInspectionDialogContent()}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={handleCloseLastInspectionDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ITEM IMAGE ZOOM DIALOG */}
      <Dialog
        open={zoomDialogOpen}
        onClose={handleCloseZoomDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Zoomed Item Photo</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              maxHeight: "80vh",
              overflow: "auto",
            }}
          >
            {zoomedImageUrl && (
              <img
                src={zoomedImageUrl}
                alt="Item zoomed"
                style={{ maxWidth: "100%", maxHeight: "80vh", cursor: "zoom-out" }}
                onClick={handleCloseZoomDialog}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseZoomDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* DELETE DIALOG */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <DialogTitle>Move to Deleted Items?</DialogTitle>
        <DialogContent>
          <Typography>
            Move {selectedRows.length} selected {selectedRows.length === 1 ? "item" : "items"}
            {" "}to Deleted Items? You can restore {selectedRows.length === 1 ? "it" : "them"} later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>Keep items</Button>
          <Button
            onClick={handleDeleteConfirmed}
            variant="contained"
            color="error"
          >
            Move {selectedRows.length} {selectedRows.length === 1 ? "item" : "items"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showRestoreConfirm}
        onClose={() => setShowRestoreConfirm(false)}
      >
        <DialogTitle>Restore selected items?</DialogTitle>
        <DialogContent>
          <Typography>
            Restore {selectedRows.length} selected {selectedRows.length === 1 ? "item" : "items"}
            {" "}to the active Equipment Register?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRestoreConfirm(false)}>Keep deleted</Button>
          <Button
            onClick={handleRestoreConfirmed}
            variant="contained"
            color="success"
          >
            Restore {selectedRows.length} {selectedRows.length === 1 ? "item" : "items"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openQrDialog} onClose={() => setOpenQrDialog(false)}>
        <DialogContent>
          <Typography variant="h6" align="center">
            QR Codes
          </Typography>
          {qrCodeUrls.length > 0 && (
            <Box>
              <img
                src={qrCodeUrls[currentPage - 1]}
                alt="QR Code"
                style={{ maxWidth: "100%", maxHeight: "300px" }}
              />
              <Typography align="center">
                Page {currentPage} of {qrCodeUrls.length}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenQrDialog(false)}>Close</Button>
          <Button onClick={handlePrintQrCode} disabled={!qrCodeUrls.length}>
            Print All
          </Button>
        </DialogActions>
      </Dialog>
      <ItemHistoryDialog
        open={historyDialogOpen}
        onClose={handleCloseHistory}
        item={historyItem}
        rows={historyRows}
        loading={historyLoading}
        error={historyError}
        onRetry={() => historyItem && loadItemHistory(historyItem)}
      />
    </>
  );
}
export default function EXregister() {
  const { user, sessionToken } = useContext(UserContext);
  const token = sessionToken === undefined ? localStorage.getItem("token") : sessionToken;
  return <RegisterWorkspace key={JSON.stringify([user?.id, user?.database_name, token])} />;
}
